import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { _axios } from "../../utils/axios";
import Multiselect from "multiselect-react-dropdown";
import { useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import userImg from "../../assets/images/user.svg";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import { DocumentPreviewDialog } from "../../components/CommonComponents/DialogConfirmation/DocPreviewDialog";
import { DocDecisionL1Button, DownloadButton, NocL1Button, ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { BASE_URL } from "../../constants/Constants";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
import { isLevel1 } from "../../hoc/isLevel1";
import Select from "../../components/CommonComponents/Select/Select";
import {
  useMoujaConfigs,
  getKhasaraRangeForMouja,
  getAsrMaxRateForMouja,
} from "../../hooks/mouja-khasara";
import {
  TApplicationField,
  TApplicationFieldOption,
} from "../../hooks/application-fields.hooks";
import {
  TApplication,
  TPopulatedForm,
  useApplicationFormSteps,
  useNameOfServiceField,
  useServiceConditionField,
} from "../../hooks/applications.hooks";
import { useToggle } from "../../hooks/common";
import dayjs from "dayjs";
import { FormProvider, useForm } from "react-hook-form";
import { isPhase2Continuation } from "../../config/phaseConstants";
import Input from "../../components/CommonComponents/Input/Input";
import APP_CONFIG from "../../config/appConfig";
import { splitCombinedKhasaraNo } from "../../hooks/mouja-khasara";
import { removeEmptyValues } from "../../utils/common";
import style from "../superAdmin/logo/logo.module.css";
import { GenerateCertificateWrapper } from "../LevelTwo/LevelTwoPaymentConfirm";
import {
  TReviewWithReviewer,
  TSavedDemandNoteData,
  useApplicationForLevel1,
  useForwardApplicationForLevel1,
  useLevel1AllReviews,
  useLevel1DecisionMaps,
  useLevel1LastReview,
  useLevel1ReturnApplication,
  useLevel1UpdateDocumentDecision,
  useLevel1UpdateNocDecision,
  useSaveLevel1Review,
  useSaveDemandNoteDataForLevel1,
  useSavedDemandNoteDataForLevel1,
} from "../../hooks/level-1-application-approval.hooks";
import { useAutoFillDemandNoteData } from "../../hooks/level-3-application-approval.hooks";
import { downloadResource } from "../../utils/common";
import { formatDate } from "../../utils/date";
import { RenderAllReviews } from "./RenderAllReviews";

export const LevelOneApplicationViewDocsAndConfirm = isLevel1(() => {
  const location = useLocation();
  const [screen, setScreen] = useState<"docs" | "review" | "generate-doc">(
    "docs"
  );



  const id = +location.state?.id;
  const decisionMapQuery = useLevel1DecisionMaps({ id });

  const {
    isError: isError1,
    isFetching: isFetching1,
    data: application,
  } = useApplicationForLevel1({ id });

  const {
    data: result,
    isError: isError2,
    isFetching: isFetching2,
  } = useApplicationFormSteps(Number(application?.name_of_service));

  const autoFill = useAutoFillDemandNoteData();

  const serviceQuery = useNameOfServiceField();
  const nameOfService = serviceQuery.data?.options?.length ?
    serviceQuery.data.options.find((e: any) => e?.id === application?.name_of_service) : ''

  const condition = useServiceConditionField({ name_of_service: (nameOfService as TApplicationFieldOption)?.name })

  const steps = result?.data || [];



  const isFetching = isFetching1 || isFetching2 || decisionMapQuery.isLoading;
  const isError =
    decisionMapQuery.isError ||
    isError1 ||
    isError2 ||
    (!isFetching && (!application || !steps?.length));

  const setStuff = useSetStuffToRenderOnNavbar();

  useEffect(() => {
    setStuff(
      <div className="md:ml-auto md:mr-8">
        <p className="text-grey-600">
          <span className="w-28 font-semibold inline-block">
            Application ID
          </span>
          <span className="text-grey-600 font-semibold mx-2">:</span>
          <span>{application?.applicationId || "Nil"}</span>
        </p>
        <p className="text-grey-600 flex items-center">
          <span className="w-28 text-grey-600 font-semibold inline-block">
            Status
          </span>
          <span className="text-grey-600 font-semibold mx-2">:</span>
          <span className="text-primary">{application?.status || "Nil"}</span>
        </p>
      </div>
    );
    return () => setStuff(null);
  }, [setStuff, application]);

  const { documentDecisions, nocDecisions } = decisionMapQuery.data || {};

  if (isNaN(id)) {
    return <Navigate to={"/user-applications"} replace />;
  }

  if (isFetching || isError || !application) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  let toRender = null;

  switch (screen) {
    case "docs": {
      toRender = (
        <ViewAndValidateDocumentsAndNOCs
          application={application}
          steps={steps}
          documentDecisions={documentDecisions}
          nocDecisions={nocDecisions}
          onNext={() => setScreen("review")}
        />
      );
      break;
    }
    case "review": {
      toRender = (
        <Review
          steps={steps}
          application={application}
          onBack={() => setScreen("docs")}
          onNext={() => setScreen("generate-doc")}
          allDocsApproved={Object.values(documentDecisions || {})
            .concat(Object.values(nocDecisions || {}))
            .every((e) => !!e)}
        />
      );
      break;
    }
    case "generate-doc": {
      toRender = (
        <GenerateDemandNoteWrapper
          application={application}
          autoFill={autoFill}
          condition={condition?.data?.[0] || {}}
          onBack={() => setScreen("docs")}
        />
      );
      break;
    }
  }

  return (
    <>
      <div className="px-4 pb-4 md:p-4">
        <BoxComponent>
          <div className="bg-white p-2 rounded-2xl">{toRender}</div>
        </BoxComponent>
      </div>
    </>
  );
});

type GenerateDemandNoteProps = {
  onBack: () => unknown;
  application: TApplication & Record<string, unknown>;
  autoFill?: any;
  condition?: any;
};

function GenerateDemandNoteWrapper({
                                     application,
                                     autoFill,
                                     condition,
                                     onBack,
                                   }: GenerateDemandNoteProps) {
  const navigate = useNavigate();

  const isPhase2 = isPhase2Continuation(application);

  if (isPhase2) {
    return (
        <GenerateCertificateWrapper
            application={application}
            autoFill={autoFill}
            condition={condition}
            onBack={onBack}
            onForward={() => navigate("/level-1-applications")}
        />
    );
  }

  // ORIGINAL CODE PRESERVED FOR COMPARISON:
  // const lastReviewQuery = useLevel2LastReview({ id: application.id });
  // const savedDemandNoteDataQuery = useSavedDemandNoteDataForLevel2({ ... });

  // UPDATED FOR L1: Uses Level 1 review hooks
  const lastReviewQuery = useLevel1LastReview({ id: application.id });
  const reviewId = lastReviewQuery.data?.review?.id;

  const savedDemandNoteDataQuery = useSavedDemandNoteDataForLevel1({
    applicationId: application.id,
    reviewId: reviewId || 0,
  });

  const isFetching =
      lastReviewQuery.isFetching || (!!reviewId && savedDemandNoteDataQuery.isFetching);
  const isError =
      lastReviewQuery.isError || (!!reviewId && savedDemandNoteDataQuery.isError);

  if (isFetching || !reviewId) {
    return (
        <div className="flex flex-col justify-center items-center p-8 space-y-3">
          <SpinnerCircular />
          <p className="text-gray-600 font-semibold">Loading demand note generator...</p>
        </div>
    );
  }

  if (isError) {
    return (
        <div className="flex justify-center items-center p-8">
          <p className="text-red-500 font-semibold">Something went wrong while fetching info!</p>
        </div>
    );
  }

  const isThereAConst = Boolean((APP_CONFIG as any)?.ENABLE_CONSTRUCTION_QUESTION) ? application.is_there_a_construction : false;

  if (isThereAConst) {
    return (
        <GenerateDemandNoteBuildingPlan
            reviewId={reviewId}
            application={application}
            autoFill={autoFill}
            condition={condition}
            onBack={onBack}
            defaultValues={savedDemandNoteDataQuery.data}
        />
    );
  } else {
    return (
        <GenerateDemandNote
            reviewId={reviewId}
            application={application}
            autoFill={autoFill}
            condition={condition}
            onBack={onBack}
            defaultValues={savedDemandNoteDataQuery.data}
        />
    );
  }
}

const KeyValue = ({ label, value }: { label: string; value?: any }) => (
    <div className="flex justify-between items-center text-sm border-b py-1">
      <span className="font-semibold text-gray-700">{label}:</span>
      <span className="font-bold text-gray-900">{typeof value === "object" ? "-" : (value ?? "-")}</span>
    </div>
);

function GenerateDemandNote({
                              defaultValues,
                              application,
                              autoFill,
                              condition,
                              onBack,
                              reviewId,
                            }: GenerateDemandNoteProps & {
  defaultValues?: TSavedDemandNoteData;
  reviewId: number;
}) {
  const navigate = useNavigate();
  const isThereAConst = Boolean((APP_CONFIG as any)?.ENABLE_CONSTRUCTION_QUESTION) ? application.is_there_a_construction : false;
  console.log("yes/no", isThereAConst);

  const [pdfToView, setPdfToView] = useState<null | string>(null);
  // UPDATED FOR L1: Uses Level 1 mutations
  const forwardMutation = useForwardApplicationForLevel1();
  const saveDataMutation = useSaveDemandNoteDataForLevel1();
  const enableNewPlotCharges = Boolean(
      (APP_CONFIG as any)?.ENABLE_BAHADURA_PLOT_DEMAND_NOTE_CHARGES
  );

  const [configRates, setConfigRates] = useState({
    rrLandRate: 0,
    rrLandRateCommercial: 0,
    rrLandRateIndustrial: 0,
    rrLandRateMixed: 0,
    rrConstructionRate: 0,
    scrutinyRate: 500,
    developmentRateResidential: 0.5,
    developmentRateIndustrial: 0.75,
    developmentRateCommercial: 1,
    developmentRateMixed: 0.5,
    compoundingMultiplier: 3,
    supervisionRate: 85,
    swmRate: 15,
    administrativeRate: 15,
    surveyMeasurementFee: 6000,
    infrastructureFeePercent: 15,
  });

  // Fetch config data on component mount
  useEffect(() => {
    const fetchConfigData = async () => {
      try {
        const response = await _axios.get(
            "/demand-note-config/get-config-for-calculations"
        );
        const data = response.data.data;
        setConfigRates({
          rrLandRate: parseFloat(data.rr_land_rate) || 0,
          rrLandRateCommercial: parseFloat(data.rr_land_rate_commercial || 0),
          rrLandRateIndustrial: parseFloat(data.rr_land_rate_industrial || 0),
          rrLandRateMixed: parseFloat(data.rr_land_rate_mixed || 0),
          rrConstructionRate: parseFloat(data.rr_construction_rate) || 0,
          scrutinyRate: parseFloat(data.scrutiny_rate) || 500,
          developmentRateResidential:
              parseFloat(data.development_rate_residential) || 0.5,
          developmentRateIndustrial:
              parseFloat(data.development_rate_industrial) || 0.75,
          developmentRateCommercial:
              parseFloat(data.development_rate_commercial) || 1,
          developmentRateMixed:
              parseFloat(data.development_rate_mixed) || 0.5,
          compoundingMultiplier: parseFloat(data.compounding_multiplier) || 3,
          supervisionRate: parseFloat(data.supervision_rate) || 85,
          swmRate: parseFloat(data.swm_rate) || 15,
          administrativeRate: parseFloat(data.administrative_rate) || 15,
          surveyMeasurementFee: parseFloat(data.survey_measurement_fee) || 6000,
          infrastructureFeePercent:
              parseFloat(data.infrastructure_fee_percent) || 15,
        });
      } catch (error) {
        console.error("Error fetching config data:", error);
      }
    };

    fetchConfigData();
  }, []);

  const formMethods = useForm<any>({
    defaultValues: {
      ...removeEmptyValues(defaultValues ?? {}),
      applicantName: defaultValues?.applicantName || application.plot_owner_s_name || "",
      layoutName: defaultValues?.layoutName || application.unauthorized_layout_name || "",
      mauja: defaultValues?.mauja || application.mauja || "",
      plotNo: defaultValues?.plotNo || application.plot_no || "",
      plotUsageType: defaultValues?.plotUsageType || "",
      area: application.area_of_plot || 0,
      constructionArea: application.construction_area || 0,
      unit_area: application.unit_area_of_plot || "sq. mt.",
      conditions: condition?.status === 1 ? condition?.condition : "",
      dueDate: defaultValues?.dueDate
          ? dayjs(defaultValues.dueDate).format("YYYY-MM-DD")
          : null,
      khasaraNo: defaultValues?.khasaraNo || application?.khasara_no || "",
      main_khasara_no:
          defaultValues?.main_khasara_no ||
          splitCombinedKhasaraNo(application?.khasara_no).main,
      manual_khasara_no:
          defaultValues?.manual_khasara_no ||
          splitCombinedKhasaraNo(application?.khasara_no).manual,
      municipality: autoFill?.data?.[0]?.municipal_council_name || "",
      tehsil: autoFill?.data?.[0]?.tahasil_name || "",
      district: autoFill?.data?.[0]?.district_name || "",
      logo: autoFill?.data?.[0]?.demand_note_logo || "",
      headerText: autoFill?.data?.[0]?.demand_note_header_text || "",
      supervisionCharges:
          defaultValues?.supervisionCharges ||
          autoFill?.data?.[0]?.supervision_charges ||
          "",
      areaOfPlot:
          defaultValues?.areaOfPlot || autoFill?.data?.[0]?.area_of_plot || "",
      scrutinyCharges: defaultValues?.scrutinyCharges || 0,
      compoundingCharges: defaultValues?.compoundingCharges || 0,
      swmCharges: defaultValues?.swmCharges || 0,
      administrativeCharges: defaultValues?.administrativeCharges || 0,
      surveyMeasurementCharges: defaultValues?.surveyMeasurementCharges || 0,
      infrastructureFee: defaultValues?.infrastructureFee || 0,
    },
    mode: "onBlur",
    shouldFocusError: true,
  });

  const { formState, watch, setValue } = formMethods;
  const moujaConfigs = useMoujaConfigs();
  const selectedMauja = watch("mauja");
  const { minLimit, maxLimit, maujaDisplayName, khasaraOptions } =
      getKhasaraRangeForMouja(moujaConfigs, selectedMauja);

  const handleKhasaraCombine = (mainVal: string, manualVal: string) => {
    const cleanManual = (manualVal || "").trim();
    setValue("manual_khasara_no", cleanManual);
    setValue("main_khasara_no", mainVal);
    if (mainVal && cleanManual) {
      setValue("khasaraNo", `${mainVal}/${cleanManual}`, { shouldValidate: true });
    } else if (mainVal) {
      setValue("khasaraNo", mainVal, { shouldValidate: true });
    } else if (cleanManual) {
      setValue("khasaraNo", cleanManual, { shouldValidate: true });
    }
  };

  const [isEditable, setIsEditable] = useState({
    particular1Total: false,
    particular2Total: false,
    totalAmount: false,
    supervisionTotal: false,
    applicantName: false,
    layoutName: false,
    mauja: false,
    khasaraNo: false,
    area: false,
    constructionArea: false,
    plotNo: false,
    scrutinyCharges: false,
    compoundingCharges: false,
    swmCharges: false,
    administrativeCharges: false,
    surveyMeasurementCharges: false,
    infrastructureFee: false,
  });

  type EditableFields =
      | "particular1Total"
      | "particular2Total"
      | "totalAmount"
      | "supervisionTotal"
      | "applicantName"
      | "layoutName"
      | "mauja"
      | "khasaraNo"
      | "area"
      | "constructionArea"
      | "plotNo"
      | "scrutinyCharges"
      | "compoundingCharges"
      | "swmCharges"
      | "administrativeCharges"
      | "surveyMeasurementCharges"
      | "infrastructureFee";
  type AllFields =
      | EditableFields
      | "particular1Rate"
      | "particular2Rate"
      | "area";

  const toggleEditable = (field: EditableFields) => {
    setIsEditable((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const onChangeHandler = (field: AllFields, event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    formMethods.setValue(field, numericValue);

    const values = formMethods.getValues();

    // Convert percentage rates to decimals
    const regularizationPercent = Number(values.particular1Rate || 0) / 100;
    const developmentPercent = Number(values.particular2Rate || 0) / 100;

    const particular1Total =
        Number(values.areaOfPlot || 0) *
        regularizationPercent *
        configRates.rrLandRate *
        3;

    const particular2Total =
        Number(values.areaOfPlot || 0) *
        developmentPercent *
        configRates.rrLandRate;

    const supervisionCharges = Number(values.supervisionCharges || 0);
    const totalAmount =
        particular1Total + particular2Total + supervisionCharges;

    if (field === "particular1Rate" || field === "area") {
      formMethods.setValue("particular1Total", particular1Total);
    }
    if (field === "particular2Rate" || field === "area") {
      formMethods.setValue("particular2Total", particular2Total);
    }
    formMethods.setValue("totalAmount", totalAmount);
  };

  const setTotalAmount = (total = 0, securityCharger: any = null) => {
    const formData = formMethods?.getValues() ?? {};
    const extrSupervisionCharger = securityCharger
        ? securityCharger
        : formData?.supervisionCharges ?? 0;

    const totalAmount = Number(total) + Number(extrSupervisionCharger || 0);
    // Format to 2 decimal places
    const formattedTotal = Math.round(totalAmount * 100) / 100;

    formMethods.setValue("totalAmount", formattedTotal);
  };

  const onAreaOfPlot = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    // Remove non-numeric characters from the input value
    const numericValue = Number(value.replace(/[^\d.]/g, ""));

    // Get plot usage type to determine which RR rate to use
    const plotUsageType = formMethods.getValues("plotUsageType");
    let rrRate = 0;

    if (plotUsageType === "Residential") {
      rrRate = configRates.rrLandRate;
    } else if (plotUsageType === "Commercial") {
      rrRate = configRates.rrLandRateCommercial;
    } else if (plotUsageType === "Industrial") {
      rrRate = configRates.rrLandRateIndustrial;
    } else if (plotUsageType === "Mixed") {
      rrRate = configRates.rrLandRateMixed;
    }

    const particular1Total = (numericValue * rrRate).toFixed(2);

    console.log("particular1Total" + particular1Total);
    console.log("particular1Rate" + formMethods?.getValues()?.particular1Rate);

    const scrutinyCharges = Number(formMethods.getValues("scrutinyCharges") || 0);
    const totalAmount = Number(particular1Total) + scrutinyCharges;

    // formMethods.setValue("areaOfPlot", numericValue);
    formMethods.setValue("areaOfPlot", numericValue);
    formMethods.setValue("particular1Total", Number(particular1Total));
    formMethods.setValue("totalAmount", totalAmount);
  };

  const onChangePlotUsageType = (event: React.ChangeEvent<any>) => {
    const plotUsageType = event.target.value;
    formMethods.setValue("plotUsageType", plotUsageType);

    // Recalculate particular1Total based on new plot usage type
    const areaOfPlot = Number(formMethods.getValues("areaOfPlot") || 0);
    let rrRate = 0;

    if (plotUsageType === "Residential") {
      rrRate = configRates.rrLandRate;
    } else if (plotUsageType === "Commercial") {
      rrRate = configRates.rrLandRateCommercial;
    } else if (plotUsageType === "Industrial") {
      rrRate = configRates.rrLandRateIndustrial;
    } else if (plotUsageType === "Mixed") {
      rrRate = configRates.rrLandRateMixed;
    }

    const particular1Total = (areaOfPlot * rrRate).toFixed(2);
    const scrutinyCharges = Number(formMethods.getValues("scrutinyCharges") || 0);
    const totalAmount = Number(particular1Total) + scrutinyCharges;

    formMethods.setValue("particular1Total", Number(particular1Total));
    formMethods.setValue("totalAmount", totalAmount);
  };

  const onChangeParticular1Total = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    // Remove non-numeric characters from the input value
    const numericValue = value.replace(/[^\d.]/g, "");
    // formMethods.setValue("particular1Total", numericValue);
    const totalAmount =
        Number(numericValue) +
        //Number(formMethods?.getValues()?.scrutinyCharges || 0) +
        Number(formMethods?.getValues()?.particular2Total || 0) +
        Number(formMethods?.getValues()?.supervisionCharges || 0);
    const formattedTotal = Math.round(totalAmount * 100) / 100;
    //formMethods.setValue("totalAmount", totalAmount);
    formMethods.setValue("totalAmount", formattedTotal);
  };

  const onChangeParticular2Total = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    // Remove non-numeric characters from the input value
    const numericValue = value.replace(/[^\d.]/g, "");

    const totalAmount =
        Number(numericValue) +
        Number(formMethods?.getValues()?.particular1Total || 0) +
        Number(formMethods?.getValues()?.supervisionCharges || 0);
    const formattedTotal = Math.round(totalAmount * 100) / 100;

    //formMethods.setValue("totalAmount", totalAmount);
    formMethods.setValue("totalAmount", formattedTotal);
  };

  const onChangeSupervisionRate = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    const roundedValue =
        Math.round(parseFloat(numericValue || "0") * 100) / 100;
    const totalAmount =
        Number(formMethods?.getValues()?.particular1Total || 0) +
        Number(formMethods?.getValues()?.particular2Total || 0);

    setTotalAmount(totalAmount, roundedValue);
  };

  const onChangeSupervisionCharges = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");

    // Set the cleaned supervision charges value
    formMethods.setValue("supervisionCharges", numericValue);

    // Fetch other values from form
    const areaOfPlot = Number(formMethods?.getValues()?.areaOfPlot || 0);
    const rrLandRate = Number(formMethods?.getValues()?.rrLandRate || 0);
    const regularizationPercent =
        Number(formMethods?.getValues()?.regularizationPercent || 0) / 100;
    const developmentPercent =
        Number(formMethods?.getValues()?.developmentPercent || 0) / 100;

    // Calculate charges
    const regularizationCharges =
        areaOfPlot * regularizationPercent * configRates.rrLandRate * 3;
    const developmentCharges = areaOfPlot * developmentPercent * rrLandRate;
    const supervisionCharges = Number(numericValue || 0);

    // Final total amount calculation
    const totalAmount =
        regularizationCharges + developmentCharges + supervisionCharges;

    // Set total amount in the form
    formMethods.setValue("totalAmount", totalAmount);
  };

  const onChangeTotalAmount = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    // Remove non-numeric characters from the input value
    const numericValue = value.replace(/[^\d.]/g, "");
    formMethods.setValue("totalAmount", numericValue);
  };

  const areaOfPlotWatch = watch("areaOfPlot");
  const plotUsageTypeWatch = watch("plotUsageType");

  useEffect(() => {
    if (!enableNewPlotCharges) return;

    const round2 = (n: number) => Math.round(n * 100) / 100;
    const areaOfPlot = Number(areaOfPlotWatch || 0);
    const asrMaxRate = getAsrMaxRateForMouja(moujaConfigs, selectedMauja);

    const developmentRate =
        plotUsageTypeWatch === "Industrial"
            ? configRates.developmentRateIndustrial
            : plotUsageTypeWatch === "Commercial"
                ? configRates.developmentRateCommercial
                : plotUsageTypeWatch === "Mixed"
                    ? configRates.developmentRateMixed
                    : configRates.developmentRateResidential;

    const isR3Zone =
        String(selectedMauja || "").trim().toLowerCase() === "gonhi";

    const computedScrutiny = round2(
        (areaOfPlot / 4000) * configRates.scrutinyRate
    );
    const computedDevelopmentTotal = round2(
        (developmentRate / 100) * asrMaxRate * areaOfPlot
    );
    const computedCompounding = round2(
        configRates.compoundingMultiplier * computedDevelopmentTotal
    );
    const computedSupervision = round2(configRates.supervisionRate * areaOfPlot);
    const computedSwm = round2(configRates.swmRate * areaOfPlot);
    const computedAdministrative = round2(
        configRates.administrativeRate * areaOfPlot
    );
    const computedSurveyMeasurement = round2(configRates.surveyMeasurementFee);
    const savedInfraFee =
        defaultValues?.infrastructureFee !== undefined &&
        defaultValues?.infrastructureFee !== null
            ? Number(defaultValues.infrastructureFee)
            : null;

    const currentTypedInfra = watch("infrastructureFee");
    const typedInfraNum = currentTypedInfra !== undefined && currentTypedInfra !== null ? Number(currentTypedInfra) : 0;
    const computedInfrastructureFee = isR3Zone
        ? round2((configRates.infrastructureFeePercent / 100) * asrMaxRate * areaOfPlot)
        : (!isNaN(typedInfraNum) && typedInfraNum > 0
            ? typedInfraNum
            : (savedInfraFee !== null && savedInfraFee > 0 ? savedInfraFee : 0));

    if (!isEditable.scrutinyCharges) setValue("scrutinyCharges", computedScrutiny);
    if (!isEditable.particular2Total) {
      setValue("particular2Rate", developmentRate);
      setValue("particular2Total", computedDevelopmentTotal);
    }
    if (!isEditable.compoundingCharges)
      setValue("compoundingCharges", computedCompounding);
    if (!isEditable.supervisionTotal)
      setValue("supervisionCharges", computedSupervision);
    if (!isEditable.swmCharges) setValue("swmCharges", computedSwm);
    if (!isEditable.administrativeCharges)
      setValue("administrativeCharges", computedAdministrative);
    if (!isEditable.surveyMeasurementCharges)
      setValue("surveyMeasurementCharges", computedSurveyMeasurement);
    if (!isEditable.infrastructureFee)
      setValue("infrastructureFee", computedInfrastructureFee);

    const scrutinyForTotal = isEditable.scrutinyCharges
        ? Number(watch("scrutinyCharges") || 0)
        : computedScrutiny;
    const developmentForTotal = isEditable.particular2Total
        ? Number(watch("particular2Total") || 0)
        : computedDevelopmentTotal;
    const compoundingForTotal = isEditable.compoundingCharges
        ? Number(watch("compoundingCharges") || 0)
        : computedCompounding;
    const supervisionForTotal = isEditable.supervisionTotal
        ? Number(watch("supervisionCharges") || 0)
        : computedSupervision;
    const swmForTotal = isEditable.swmCharges
        ? Number(watch("swmCharges") || 0)
        : computedSwm;
    const administrativeForTotal = isEditable.administrativeCharges
        ? Number(watch("administrativeCharges") || 0)
        : computedAdministrative;
    const surveyForTotal = isEditable.surveyMeasurementCharges
        ? Number(watch("surveyMeasurementCharges") || 0)
        : computedSurveyMeasurement;
    const infrastructureForTotal = isEditable.infrastructureFee
        ? Number(watch("infrastructureFee") || 0)
        : computedInfrastructureFee;

    if (!isEditable.totalAmount) {
      setValue(
          "totalAmount",
          round2(
              scrutinyForTotal +
              developmentForTotal +
              compoundingForTotal +
              supervisionForTotal +
              swmForTotal +
              administrativeForTotal +
              surveyForTotal +
              infrastructureForTotal
          )
      );
    }
    // eslint-disable-next-line
  }, [
    enableNewPlotCharges,
    areaOfPlotWatch,
    plotUsageTypeWatch,
    selectedMauja,
    configRates,
    isEditable,
  ]);

  const [isEditableConditions, setIsEditableConditions] = useState(false);
  const toggleEditableConditions = () => {
    setIsEditableConditions((prev) => !prev);
  };

  return (
      <FormProvider {...formMethods}>
        <h4
            className={`text-center text-grey-600 font-semibold mb-8 text-2xl ${pdfToView ? "hidden" : ""
            }`}
        >
          Demand Note - Site Plan
        </h4>

        <form
            id="create-demand-note"
            className={`grid grid-cols-2 gap-4 ${pdfToView ? "hidden" : ""}`}
            noValidate
            onSubmit={formMethods.handleSubmit((data) =>
                saveDataMutation.mutate(
                    {
                      applicationId: application.id,
                      reviewId,
                      data,
                      isThereAConst: isThereAConst ?? false,
                    },
                    {
                      onSuccess: (result) => {
                        const blob = new Blob([result.data], { type: "application/pdf" });
                        const url = URL.createObjectURL(blob);
                        setPdfToView(url);
                      },
                    }
                )
            )}
        >
          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1">
              <b>Applicant Name</b>
            </span>
              <i
                  className={`fa ${isEditable.applicantName ? "fa-lock" : "fa-pencil"
                  } cursor-pointer`}
                  onClick={() => toggleEditable("applicantName")}
              ></i>
            </label>
            <Input
                label=""
                placeholder="Applicant Name"
                {...formMethods.register("applicantName")}
                disabled={!isEditable.applicantName}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1">
              <b>Name of Layout to be regularized</b>
            </span>
              <i
                  className={`fa ${isEditable.layoutName ? "fa-lock" : "fa-pencil"
                  } cursor-pointer`}
                  onClick={() => toggleEditable("layoutName")}
              ></i>
            </label>
            <Input
                label=""
                placeholder="Name of Layout to be regularized"
                {...formMethods.register("layoutName", {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    const val = e.target.value;
                    const cleanVal = val.replace(/[^a-zA-Z0-9 ]/g, "");
                    formMethods.setValue("layoutName", cleanVal, { shouldValidate: true });
                  },
                })}
                disabled={!isEditable.layoutName}
            />
          </div>

          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1">
              <b>Mauza</b>
            </span>
              <i
                  className={`fa ${isEditable.mauja ? "fa-lock" : "fa-pencil"
                  } cursor-pointer`}
                  onClick={() => toggleEditable("mauja")}
              ></i>
            </label>
            <Select
                label=""
                {...formMethods.register("mauja")}
                disabled={!isEditable.mauja}
            >
              <option value="" disabled hidden>
                Select Mouza
              </option>
              {Array.from(
                  new Set([
                    ...moujaConfigs.map((m) => m.mouja_name).filter(Boolean),
                    ...(selectedMauja &&
                    !moujaConfigs.some((m) => m.mouja_name === selectedMauja)
                        ? [selectedMauja as string]
                        : []),
                  ])
              ).map((name) => (
                  <option key={`l2-mauja-${name}`} value={name}>
                    {name}
                  </option>
              ))}
            </Select>
          </div>

          <div className="col-span-2 md:col-span-1 space-y-3">
            <label className="flex items-center">
            <span className="mr-2 p-1">
              <b>Khasara No</b>
            </span>
              <i
                  className={`fa ${isEditable.khasaraNo ? "fa-lock" : "fa-pencil"
                  } cursor-pointer`}
                  onClick={() => toggleEditable("khasaraNo")}
              ></i>
            </label>
            <Select
                label=""
                disabled={!isEditable.khasaraNo || !selectedMauja}
                helperText={!selectedMauja ? "Please select Mouja first" : ""}
                value={watch("main_khasara_no") || ""}
                onChange={(e: any) => {
                  handleKhasaraCombine(e.target.value, watch("manual_khasara_no"));
                }}
            >
              <option value="" disabled hidden>
                {selectedMauja
                    ? `Select Khasara (${minLimit} to ${maxLimit})`
                    : "Please select Mouja first"}
              </option>
              {khasaraOptions.map((num) => (
                  <option key={`l2-khasara-${num}`} value={String(num)}>
                    Khasara No. {num} ({maujaDisplayName})
                  </option>
              ))}
            </Select>
            <Input
                label="Sub-Khasara No."
                placeholder="Manual Sub-Khasara (e.g. 55/65)"
                disabled={!isEditable.khasaraNo}
                value={watch("manual_khasara_no") || ""}
                onChange={(e) =>
                    handleKhasaraCombine(watch("main_khasara_no"), e.target.value)
                }
            />
          </div>

          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1">
              <b>Plot Area</b>
            </span>
              <i
                  className={`fa ${isEditable.area ? "fa-lock" : "fa-pencil"
                  } cursor-pointer`}
                  onClick={() => toggleEditable("area")}
              ></i>
            </label>
            <div className="flex items-end justify-between gap-4">
              <Input
                  type="number"
                  onWheel={(e) => {
                    e.currentTarget.blur();
                    e.stopPropagation();
                    setTimeout(() => e.currentTarget.focus(), 0);
                  }}
                  containerClass="basis-3/5 md:basis-3/4"
                  label=""
                  placeholder="Plot Area"
                  {...formMethods.register("area", { valueAsNumber: true })}
                  disabled={!isEditable.area}
              />
              {/*
            <Select
              containerClass="basis-2/5 md:basis-1/4"
              {...formMethods.register("unit_area")}
              disabled={!isEditable.area}
            >
              <option hidden></option>
              <option value="sq. mt.">sq. mt.</option>
              <option value="sq. ft.">sq. ft.</option>
            </Select>
            */}

              <Input
                  containerClass="basis-2/5 md:basis-1/4"
                  value="sq. mt."
                  disabled={true}
                  readOnly={true}
                  {...formMethods.register("unit_area", { value: "sq. mt." })}
              />
            </div>
          </div>

          {Boolean((APP_CONFIG as any)?.ENABLE_CONSTRUCTION_QUESTION) &&
              Boolean(application?.is_there_a_construction) && (
                  <div className="col-span-2 md:col-span-1">
                    <div className="flex items-center justify-between">
                      <label className="text-base font-bold">
                        Built-up Area (Sq. Mt.)
                      </label>
                      <i
                          className={`fa ${isEditable.constructionArea ? "fa-lock" : "fa-pencil"
                          } cursor-pointer`}
                          onClick={() => toggleEditable("constructionArea")}
                      ></i>
                    </div>
                    <Input
                        type="number"
                        onWheel={(e) => {
                          e.currentTarget.blur();
                          e.stopPropagation();
                          setTimeout(() => e.currentTarget.focus(), 0);
                        }}
                        label=""
                        placeholder="Built-up Area (sq. mt.)"
                        {...formMethods.register("constructionArea", {
                          valueAsNumber: true,
                        })}
                        disabled={!isEditable.constructionArea}
                    />
                  </div>
              )}

          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1">
              <b>Plot No</b>
            </span>
              <i
                  className={`fa ${isEditable.plotNo ? "fa-lock" : "fa-pencil"
                  } cursor-pointer`}
                  onClick={() => toggleEditable("plotNo")}
              ></i>
            </label>
            <Input
                label=""
                placeholder="Plot No"
                {...formMethods.register("plotNo")}
                disabled={!isEditable.plotNo}
            />
          </div>


          <div className="col-span-2 md:col-span-1">
            <Input
                type="date"
                label="Due Date"
                placeholder="Due Date"
                {...formMethods.register("dueDate", { valueAsDate: true })}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Select
                required
                label="Plot Usage Type"
                placeholder="Select Plot Usage Type"
                error={!!formState.errors?.plotUsageType}
                helperText={formState?.errors?.plotUsageType?.message?.toString()}
                {...formMethods.register("plotUsageType", {
                  required: "Plot Usage Type is required",
                })}
                onChange={onChangePlotUsageType}
            >
              <option value="Residential">Residential</option>
              <option value="Commercial">Commercial</option>
              <option value="Industrial">Industrial</option>
              <option value="Mixed">Mixed</option>
            </Select>
          </div>

          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1">
              <b>Area of Plot to be Regularized <span className="text-red-500">*</span></b>
            </span>
            </label>
            <Input
                required
                type="number"
                step="0.001"
                min={0}
                onWheel={(e) => {
                  e.currentTarget.blur();
                  e.stopPropagation();
                  setTimeout(() => {
                    e.currentTarget.focus();
                  }, 0);
                }}
                label=""
                placeholder="Area of Plot to be Regularized"
                error={!!formState.errors?.areaOfPlot}
                helperText={formState?.errors?.areaOfPlot?.message?.toString()}
                {...formMethods.register("areaOfPlot", {
                  required: "* Required",
                  valueAsNumber: true,
                  validate: (value) =>
                      /^\d+(\.\d{1,3})?$/.test(value?.toString()) ||
                      "Maximum three digits allowed after decimal",
                })}
                onChange={onAreaOfPlot}
                // onChange={onChangeSupervisionRate}
            />
          </div>
          {!enableNewPlotCharges ? (
              <>
                <div className="col-span-2 md:col-span-1 hidden">
                  <Input
                      type="number"
                      onWheel={(e) => {
                        e.currentTarget.blur();
                        e.stopPropagation();
                        setTimeout(() => {
                          e.currentTarget.focus();
                        }, 0);
                      }}
                      label="Supervision Charges"
                      placeholder="Supervision Charges"
                      {...formMethods.register("supervisionCharges", {
                        valueAsNumber: true,
                      })}
                      onChange={onChangeSupervisionRate}
                  />
                </div>

                <div className="col-span-2 md:col-span-1 hidden">
                  <Input
                      type="number"
                      onWheel={(e) => {
                        e.currentTarget.blur();
                        e.stopPropagation();
                        setTimeout(() => {
                          e.currentTarget.focus();
                        }, 0);
                      }}
                      label="% of R.R Land Rate for Regularization Charges"
                      placeholder="% of R.R Land Rate for Regularization Charges"
                      {...formMethods.register("particular1Rate", {
                        valueAsNumber: true,
                      })}
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label className="flex items-center">
                <span className="mr-2 p-1">
                  <b>Total regularization charges for plot</b>
                </span>
                    <i
                        className={`fa ${isEditable.particular1Total ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                        onClick={() => toggleEditable("particular1Total")}
                    />
                  </label>

                  <Input
                      required
                      type="number"
                      onWheel={(e) => {
                        e.currentTarget.blur();
                        e.stopPropagation();
                        setTimeout(() => {
                          e.currentTarget.focus();
                        }, 0);
                      }}
                      label=""
                      placeholder="Total regularization charges for plot"
                      error={!!formState.errors?.particular1Total}
                      helperText={formState?.errors?.particular1Total?.message?.toString()}
                      {...formMethods.register("particular1Total", {
                        required: "Required",
                        valueAsNumber: true,
                      })}
                      onChange={(e) => {
                        onChangeHandler("particular1Total", e);
                        onChangeParticular1Total(e);
                      }}
                      disabled={!isEditable.particular1Total}
                  />
                </div>

                <div className="col-span-2 md:col-span-1 hidden">
                  <Input
                      type="number"
                      onWheel={(e) => {
                        e.currentTarget.blur();
                        e.stopPropagation();
                        setTimeout(() => {
                          e.currentTarget.focus();
                        }, 0);
                      }}
                      label="% of R.R Land Rate for Development Charges"
                      placeholder="% of R.R Land Rate for Development Charges"
                      {...formMethods.register("particular2Rate", {
                        valueAsNumber: true,
                      })}
                  />
                </div>

                <div className="col-span-2 md:col-span-1 hidden">
                  <label className="flex items-center">
                <span className="mr-2 p-1">
                  <b>Development charges for residential plot Total</b>
                </span>
                    <i
                        className={`fa ${isEditable.particular2Total ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                        onClick={() => toggleEditable("particular2Total")}
                    />
                  </label>
                  <Input
                      type="number"
                      onWheel={(e) => {
                        e.currentTarget.blur();
                        e.stopPropagation();
                        setTimeout(() => {
                          e.currentTarget.focus();
                        }, 0);
                      }}
                      label=""
                      placeholder="Development charges for residential plot Total"
                      {...formMethods.register("particular2Total", {
                        valueAsNumber: true,
                      })}
                      onChange={(e) => {
                        onChangeHandler("particular2Total", e);
                        onChangeParticular2Total(e);
                      }}
                      disabled={!isEditable.particular2Total}
                  />
                </div>
              </>
          ) : (
              <>
                <div className="col-span-2 md:col-span-1 hidden">
                  <Input
                      type="number"
                      label="% of RR/ASR for Development Charges"
                      placeholder="% of RR/ASR for Development Charges"
                      {...formMethods.register("particular2Rate", {
                        valueAsNumber: true,
                      })}
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label className="flex items-center">
                <span className="mr-2 p-1">
                  <b>Scrutiny Charges</b>
                </span>
                    <i
                        className={`fa ${isEditable.scrutinyCharges ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                        onClick={() => toggleEditable("scrutinyCharges")}
                    />
                  </label>
                  <Input
                      type="number"
                      label=""
                      placeholder="Scrutiny Charges"
                      {...formMethods.register("scrutinyCharges", {
                        valueAsNumber: true,
                      })}
                      disabled={!isEditable.scrutinyCharges}
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label className="flex items-center">
                <span className="mr-2 p-1">
                  <b>Development Charges</b>
                </span>
                    <i
                        className={`fa ${isEditable.particular2Total ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                        onClick={() => toggleEditable("particular2Total")}
                    />
                  </label>
                  <Input
                      type="number"
                      label=""
                      placeholder="Development Charges"
                      {...formMethods.register("particular2Total", {
                        valueAsNumber: true,
                      })}
                      disabled={!isEditable.particular2Total}
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label className="flex items-center">
                <span className="mr-2 p-1">
                  <b>Compounding Charges</b>
                </span>
                    <i
                        className={`fa ${isEditable.compoundingCharges ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                        onClick={() => toggleEditable("compoundingCharges")}
                    />
                  </label>
                  <Input
                      type="number"
                      label=""
                      placeholder="Compounding Charges"
                      {...formMethods.register("compoundingCharges", {
                        valueAsNumber: true,
                      })}
                      disabled={!isEditable.compoundingCharges}
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label className="flex items-center">
                <span className="mr-2 p-1">
                  <b>Supervision Charges</b>
                </span>
                    <i
                        className={`fa ${isEditable.supervisionTotal ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                        onClick={() => toggleEditable("supervisionTotal")}
                    />
                  </label>
                  <Input
                      type="number"
                      label=""
                      placeholder="Supervision Charges"
                      {...formMethods.register("supervisionCharges", {
                        valueAsNumber: true,
                      })}
                      disabled={!isEditable.supervisionTotal}
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label className="flex items-center">
                <span className="mr-2 p-1">
                  <b>SWM Charges</b>
                </span>
                    <i
                        className={`fa ${isEditable.swmCharges ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                        onClick={() => toggleEditable("swmCharges")}
                    />
                  </label>
                  <Input
                      type="number"
                      label=""
                      placeholder="SWM Charges"
                      {...formMethods.register("swmCharges", {
                        valueAsNumber: true,
                      })}
                      disabled={!isEditable.swmCharges}
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label className="flex items-center">
                <span className="mr-2 p-1">
                  <b>Administrative Charges</b>
                </span>
                    <i
                        className={`fa ${isEditable.administrativeCharges ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                        onClick={() => toggleEditable("administrativeCharges")}
                    />
                  </label>
                  <Input
                      type="number"
                      label=""
                      placeholder="Administrative Charges"
                      {...formMethods.register("administrativeCharges", {
                        valueAsNumber: true,
                      })}
                      disabled={!isEditable.administrativeCharges}
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label className="flex items-center">
                <span className="mr-2 p-1">
                  <b>Survey / Measurement Charges</b>
                </span>
                    <i
                        className={`fa ${isEditable.surveyMeasurementCharges ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                        onClick={() => toggleEditable("surveyMeasurementCharges")}
                    />
                  </label>
                  <Input
                      type="number"
                      label=""
                      placeholder="Survey / Measurement Charges"
                      {...formMethods.register("surveyMeasurementCharges", {
                        valueAsNumber: true,
                      })}
                      disabled={!isEditable.surveyMeasurementCharges}
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label className="flex items-center">
                <span className="mr-2 p-1">
                  <b>Infrastructure Fee (R3 Zone)</b>
                </span>
                    <i
                        className={`fa ${isEditable.infrastructureFee ? "fa-lock" : "fa-pencil"} cursor-pointer`}
                        onClick={() => toggleEditable("infrastructureFee")}
                    />
                  </label>
                  <Input
                      type="number"
                      label=""
                      placeholder="Infrastructure Fee"
                      {...formMethods.register("infrastructureFee", {
                        valueAsNumber: true,
                      })}
                      disabled={!isEditable.infrastructureFee}
                  />
                </div>
              </>
          )}

          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1">
              <b> Total Demand Note Amount</b>{" "}
            </span>
              <i
                  className={`fa ${isEditable.totalAmount ? "fa-lock" : "fa-pencil"
                  } m-1 cursor-pointer`}
                  onClick={() => toggleEditable("totalAmount")}
              ></i>
            </label>
            <Input
                required
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();

                  e.stopPropagation();

                  setTimeout(() => {
                    e.currentTarget.focus();
                  }, 0);
                }}
                label=""
                placeholder="Total Demand Note Amount"
                error={!!formState.errors?.totalAmount}
                helperText={formState?.errors?.totalAmount?.message?.toString()}
                {...formMethods.register("totalAmount", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                // onChange={(e) => onChangeHandler('totalAmount', e)}
                onChange={onChangeTotalAmount}
                disabled={!isEditable.totalAmount}
            />
          </div>

          <div className="col-span-2 md:col-span-1">
            <Input
                disabled
                label="Municipal Council Name"
                placeholder="Municipal Council Name"
                error={!!formState.errors?.municipality}
                helperText={formState?.errors?.municipality?.message?.toString()}
                {...formMethods.register("municipality", {
                  required: "* Required",
                })}
            />
          </div>

          <div className="col-span-2 md:col-span-1">
            <Input
                disabled
                label="Office Address: Tehsil"
                placeholder="Office Address: Tehsil"
                error={!!formState.errors?.tehsil}
                helperText={formState?.errors?.tehsil?.message?.toString()}
                {...formMethods.register("tehsil", { required: "* Required" })}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                disabled
                label="Office Address: District"
                placeholder="Office Address: District"
                error={!!formState.errors?.district}
                helperText={formState?.errors?.district?.message?.toString()}
                {...formMethods.register("district", { required: "* Required" })}
            />
          </div>
          <div className="col-span-2">
            <label className="flex items-center">
            <span className="text-base font-bold mb-2">
              Conditions For Demand Note
            </span>
              <i
                  className={`fa ${isEditableConditions ? "fa-lock" : "fa-pencil"
                  } cursor-pointer ml-2`}
                  onClick={toggleEditableConditions}
              ></i>
            </label>
            <textarea
                placeholder="Conditions for Demand Note"
                {...formMethods.register("conditions")}
                rows={5}
                className={`h-auto w-100 ${isEditableConditions ? "bg-white" : "bg-gray-200"
                }`}
                disabled={!isEditableConditions}
            />
          </div>
          <div className="col-span-2 flex justify-center space-x-4 items-center mt-4">
            <button
                type="button"
                onClick={onBack}
                disabled={saveDataMutation.isPending || forwardMutation.isPending}
                className="bg-black px-4 md:px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              Back
            </button>
            <button
                form="create-demand-note"
                type="submit"
                disabled={saveDataMutation.isPending || forwardMutation.isPending}
                className="bg-primary px-4 md:px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              {"Save And Preview"}
            </button>
          </div>
        </form>
        {!!pdfToView && (
            <>
              <iframe
                  title="PDF Viewer"
                  // className="w-full h-full min-h-screen hidden sm:block"
                  className={style.IFrameCss}
                  src={pdfToView + "#toolbar=0"}
              />
              <div className="hidden">
                <p>Demand Note</p>
                <a
                    className="inline-block text-white py-2 px-4 rounded-full bg-blue-500"
                    download={`${application.applicationId}-Demand-Note.pdf`}
                    href={pdfToView}
                >
                  Download
                </a>
              </div>
              <div className="col-span-2 flex justify-center space-x-4 items-center mt-8">
                <button
                    type="button"
                    onClick={() => setPdfToView(null)}
                    disabled={saveDataMutation.isPending || forwardMutation.isPending}
                    className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                >
                  Back
                </button>
                <button
                    form="create-demand-note"
                    type="submit"
                    onClick={(data) =>
                        forwardMutation.mutate(
                            {
                              applicationId: application.id,
                              reviewId,
                              isThereAConst: isThereAConst ?? false,
                            },
                            { onSuccess: () => navigate("/level-1-applications") }
                        )
                    }
                    disabled={saveDataMutation.isPending || forwardMutation.isPending}
                    className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                >
                  {forwardMutation.isPending ? "Forwarding" : "Forward"}
                </button>
              </div>
            </>
        )}
      </FormProvider>
  );
}

function GenerateDemandNoteBuildingPlan({
                                          defaultValues,
                                          application,
                                          autoFill,
                                          condition,
                                          onBack,
                                          reviewId,
                                        }: GenerateDemandNoteProps & {
  defaultValues?: TSavedDemandNoteData;
  reviewId: number;
}) {
  const navigate = useNavigate();
  const isThereAConst = Boolean((APP_CONFIG as any)?.ENABLE_CONSTRUCTION_QUESTION) ? application.is_there_a_construction : false;
  console.log("yes/no", isThereAConst);

  const [pdfToView, setPdfToView] = useState<null | string>(null);
  // UPDATED FOR L1: Uses Level 1 mutations
  const forwardMutation = useForwardApplicationForLevel1();
  const saveDataMutation = useSaveDemandNoteDataForLevel1();
  const [configRates, setConfigRates] = useState({
    rrLandRate: 0,
    rrConstructionRate: 0,
  });

  useEffect(() => {
    const fetchConfigData = async () => {
      try {
        const response = await _axios.get(
            "/demand-note-config/get-config-for-calculations"
        );
        const data = response.data.data;
        setConfigRates({
          rrLandRate: parseFloat(data.rr_land_rate) || 0,
          rrConstructionRate: parseFloat(data.rr_construction_rate) || 0,
        });
      } catch (error) {
        console.error("Error fetching config data:", error);
      }
    };

    fetchConfigData();
  }, []);

  const formMethods = useForm<any>({
    defaultValues: {
      ...removeEmptyValues(defaultValues ?? {}),
      applicantName: defaultValues?.applicantName || application.plot_owner_s_name || "",
      layoutName: defaultValues?.layoutName || application.unauthorized_layout_name || "",
      mauja: defaultValues?.mauja || application.mauja || "",
      plotNo: defaultValues?.plotNo || application.plot_no || "",
      area: application.area_of_plot || 0,
      unit_area: application.unit_area_of_plot || "sq. mt.",
      constructionArea: application.construction_area || 0,
      conditions: condition?.status === 1 ? condition?.condition : "",
      dueDate: defaultValues?.dueDate
          ? dayjs(defaultValues.dueDate).format("YYYY-MM-DD")
          : null,
      khasaraNo: defaultValues?.khasaraNo || application?.khasara_no || "",
      main_khasara_no:
          defaultValues?.main_khasara_no ||
          splitCombinedKhasaraNo(application?.khasara_no).main,
      manual_khasara_no:
          defaultValues?.manual_khasara_no ||
          splitCombinedKhasaraNo(application?.khasara_no).manual,
      municipality: autoFill?.data?.[0]?.municipal_council_name || "",
      tehsil: autoFill?.data?.[0]?.tahasil_name || "",
      district: autoFill?.data?.[0]?.district_name || "",
      logo: autoFill?.data?.[0]?.demand_note_logo || "",
      headerText: autoFill?.data?.[0]?.demand_note_header_text || "",
      chargesForExcessBuiltUpArea:
          defaultValues?.chargesForExcessBuiltUpArea || "",
      percentRRLandRateForExcessBuiltUpArea:
          defaultValues?.percentRRLandRateForExcessBuiltUpArea || "",
      supervisionChargesForOpenPlot:
          defaultValues?.supervisionChargesForOpenPlot || "",
      supervisionChargesForConstruction:
          defaultValues?.supervisionChargesForConstruction || "",
      areaOfPlot:
          defaultValues?.areaOfPlot || autoFill?.data?.[0]?.area_of_plot || "",
      areaOfDemolish: defaultValues?.areaOfDemolish || "",
      areaOfExcessBuiltUp: defaultValues?.areaOfExcessBuiltUp || "",
      excessBuiltUpArea: defaultValues?.excessBuiltUpArea || "",
    },
    mode: "onBlur",
    shouldFocusError: true,
  });

  const { formState } = formMethods;

  const [isEditable, setIsEditable] = useState({
    particular1Total: false,
    particular2Total: false,
    totalAmount: false,
    buildingDevelopmentRRLandTotal: false,
    labourCessRRConstructionTotal: false,
    chargesForConstructioInSideMargineRRLandTotal: false,
    chargesForExcessBuiltUpArea: false,
  });

  type EditableFields =
      | "particular1Total"
      | "particular2Total"
      | "totalAmount"
      | "buildingDevelopmentRRLandTotal"
      | "labourCessRRConstructionTotal"
      | "chargesForConstructioInSideMargineRRLandTotal"
      | "chargesForExcessBuiltUpArea";

  type AllFields =
      | EditableFields
      | "particular1Rate"
      | "particular2Rate"
      | "area"
      | "areaOfPlot"
      | "buildingDevelopmentRRLandRate"
      | "labourCessRRConstructionRate"
      | "chargesForConstructioInSideMargineRRLandRate"
      | "percentRRLandRateForExcessBuiltUpArea"
      | "areaOfDemolish"
      | "areaOfExcessBuiltUp"
      | "excessBuiltUpArea"
      | "supervisionChargesForOpenPlot"
      | "supervisionChargesForConstruction"
      | "constructionArea"
      | "rainWaterHarwesting"
      | "securityDeposite";

  const toggleEditable = (field: EditableFields) => {
    setIsEditable((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const onChangeHandler = (field: AllFields, event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    // formMethods.setValue(field, numericValue);

    const particular1Total =
        Number(formMethods.getValues("particular1Rate") || 0) *
        Number(formMethods.getValues("area") || 1);
    const particular2Total =
        Number(formMethods.getValues("particular2Rate") || 0) *
        Number(formMethods.getValues("area") || 0);
    const buildingDevelopmentRRLandTotal =
        Number(formMethods.getValues("buildingDevelopmentRRLandRate") || 0) *
        Number(formMethods.getValues("area") || 0);
    const labourCessRRConstructionTotal =
        Number(formMethods.getValues("labourCessRRConstructionRate") || 0) *
        Number(formMethods.getValues("area") || 0);
    const chargesForConstructioInSideMargineRRLandTotal =
        Number(
            formMethods.getValues("chargesForConstructioInSideMargineRRLandRate") ||
            0
        ) * Number(formMethods.getValues("area") || 0);
    const areaExcessOfBasicFSI = Number(
        formMethods.getValues("areaExcessOfBasicFSI") || 0
    );
    const rainWaterHarwesting = Number(
        formMethods.getValues("rainWaterHarwesting") || 0
    );
    const securityDeposite = Number(
        formMethods.getValues("securityDeposite") || 0
    );
    // const scrutinyCharges = Number(
    //   formMethods.getValues("scrutinyCharges") || 0
    // );
    const totalAmount =
        particular1Total +
        particular2Total +
        buildingDevelopmentRRLandTotal +
        labourCessRRConstructionTotal +
        chargesForConstructioInSideMargineRRLandTotal +
        areaExcessOfBasicFSI +
        rainWaterHarwesting +
        securityDeposite;
    // +
    // scrutinyCharges;

    if (field === "particular1Rate" || field === "area") {
      formMethods.setValue("particular1Total", particular1Total);
    }
    if (field === "particular2Rate" || field === "area") {
      formMethods.setValue("particular2Total", particular2Total);
    }
    if (field === "buildingDevelopmentRRLandRate" || field === "area") {
      // formMethods.setValue(
      //   "buildingDevelopmentRRLandTotal",
      //   buildingDevelopmentRRLandTotal
      // );
    }
    if (field === "labourCessRRConstructionRate" || field === "area") {
      // formMethods.setValue(
      //   "labourCessRRConstructionTotal",
      //   labourCessRRConstructionTotal
      // );
    }
    if (
        field === "chargesForConstructioInSideMargineRRLandRate" ||
        field === "area"
    ) {
      // formMethods.setValue(
      //   "chargesForConstructioInSideMargineRRLandTotal",
      //   chargesForConstructioInSideMargineRRLandTotal
      // );
    }
    formMethods.setValue("totalAmount", totalAmount);
  };


  const onChangeParticular1Rate = (event: React.ChangeEvent<any>) => {
    const formData = formMethods?.getValues() ?? {};
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    const roundedValue =
        Math.round(parseFloat(numericValue || "0") * 100) / 100;
    const values = formMethods.getValues();

    const areaOfPlot = Number(formData?.areaOfPlot) || 0;

    const particular1Total = calculateRegularizationAmount(
        areaOfPlot,
        configRates?.rrLandRate,
        roundedValue
        //Number(value) || 0
    );

    formMethods.setValue("particular1Total", particular1Total);
    recalculateTotal({
      ...values,
      particular1Rate: numericValue,
      particular1Total,
    });
  };

  const onChangeParticular1Total = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    // Remove non-numeric characters from the input value
    const numericValue = value.replace(/[^\d.]/g, "");
    const values = formMethods.getValues();

    recalculateTotal({
      ...values,
      particular1Total: numericValue,
    });
  };

  const onChangeParticular2Rate = (event: React.ChangeEvent<any>) => {
    const formData = formMethods?.getValues() ?? {};
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    const roundedValue =
        Math.round(parseFloat(numericValue || "0") * 100) / 100;
    const values = formMethods.getValues();

    const areaOfPlot = Number(formData?.areaOfPlot) || 0;

    const particular2Total = calculateDevelopmentAmount(
        areaOfPlot,
        configRates?.rrLandRate,
        roundedValue
        //Number(value) || 0
    );

    formMethods.setValue("particular2Total", particular2Total);
    recalculateTotal({
      ...values,
      particular2Rate: numericValue,
      particular2Total,
    });
  };

  const onChangeParticular2Total = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    // Remove non-numeric characters from the input value
    const numericValue = value.replace(/[^\d.]/g, "");
    const values = formMethods.getValues();

    recalculateTotal({
      ...values,
      particular2Total: numericValue,
    });
  };

  const onChangeScrutinyCharges = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    // Remove non-numeric characters from the input value
    const numericValue = value.replace(/[^\d.]/g, "");
    //formMethods.setValue("scrutinyCharges", numericValue);
    const totalAmount =
        Number(numericValue) +
        Number(formMethods?.getValues()?.particular1Total || 0) +
        Number(formMethods?.getValues()?.particular2Total || 0) +
        Number(formMethods?.getValues()?.buildingDevelopmentRRLandTotal || 0) +
        Number(formMethods?.getValues()?.labourCessRRConstructionTotal || 0) +
        Number(
            formMethods?.getValues()
                ?.chargesForConstructioInSideMargineRRLandTotal || 0
        ) +
        Number(formMethods?.getValues()?.areaExcessOfBasicFSI || 0) +
        Number(formMethods?.getValues()?.rainWaterHarwesting || 0);
    formMethods.setValue("totalAmount", totalAmount);
  };

  const onChangeBuildingDevelopmentRRLandRate = (
      event: React.ChangeEvent<any>
  ) => {
    const value = event.target.value.trim();
    //const numericValue = Number(value.replace(/[^\d.]/g, ""));
    const numericValue = value.replace(/[^\d.]/g, "");
    const roundedValue =
        Math.round(parseFloat(numericValue || "0") * 100) / 100;
    const values = formMethods.getValues();

    const constructionArea = Number(
        formMethods.getValues().constructionArea || 0
    );
    const buildingDevelopmentTotal = calculateBuildingDevelopmentCharges(
        constructionArea,
        configRates?.rrLandRate, // Use rrLandRate for building development
        //numericValue
        roundedValue
    );

    formMethods.setValue(
        "buildingDevelopmentRRLandTotal",
        buildingDevelopmentTotal
    );
    recalculateTotal({
      ...values,
      buildingDevelopmentRRLandRate: numericValue,
      buildingDevelopmentRRLandTotal: buildingDevelopmentTotal,
    });
  };

  const onChangeBuildingDevelopmentRRLandTotal = (
      event: React.ChangeEvent<any>
  ) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    formMethods.setValue("buildingDevelopmentRRLandTotal", numericValue);
    //recalculateTotal();
  };

  const onChangeLabourCessRRConstructionRate = (
      event: React.ChangeEvent<any>
  ) => {
    const value = event.target.value.trim();
    // const numericValue = Number(value.replace(/[^\d.]/g, ""));
    const numericValue = value.replace(/[^\d.]/g, "");
    const roundedValue =
        Math.round(parseFloat(numericValue || "0") * 100) / 100;
    // formMethods.setValue("labourCessRRConstructionRate", roundedValue);

    //formMethods.setValue("labourCessRRConstructionRate", numericValue);
    const values = formMethods.getValues();
    const constructionArea = Number(
        formMethods.getValues().constructionArea || 0
    );
    const labourCessTotal = calculateLabourCessCharges(
        constructionArea,
        configRates?.rrConstructionRate, // Use rrConstructionRate for labour cess
        //numericValue
        roundedValue
    );

    formMethods.setValue("labourCessRRConstructionTotal", labourCessTotal);
    recalculateTotal({
      ...values,
      labourCessRRConstructionRate: numericValue,
      labourCessRRConstructionTotal: labourCessTotal,
    });
  };

  const onChangeLabourCessRRConstructionTotal = (
      event: React.ChangeEvent<any>
  ) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    formMethods.setValue("labourCessRRConstructionTotal", numericValue);
    //recalculateTotal();
  };

  // Add these onChange handler functions to your Building Plan component
  // Place them after your existing onChange functions like onChangeParticular2Total

  // 1. Handler for Charges for Excess Built Up Area
  const onChangeChargesForExcessBuiltUpArea = (
      event: React.ChangeEvent<any>
  ) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    // formMethods.setValue("chargesForExcessBuiltUpArea", numericValue);
    const values = formMethods.getValues();

    recalculateTotal({
      ...values,
      chargesForExcessBuiltUpArea: numericValue,
    });
  };

  // 2. Handler for % of R.R Land Rate for Excess Built Up Area
  const onChangePercentRRLandRateForExcessBuiltUpArea = (
      event: React.ChangeEvent<any>
  ) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    const roundedValue =
        Math.round(parseFloat(numericValue || "0") * 100) / 100;
    // formMethods.setValue("percentRRLandRateForExcessBuiltUpArea", numericValue);
    // formMethods.setValue("percentRRLandRateForExcessBuiltUpArea", roundedValue);
    const values = formMethods.getValues();

    // Recalculate charges for excess built up area based on formula
    const areaOfExcessBuiltUp = Number(
        formMethods.getValues().areaOfExcessBuiltUp || 0
    );
    const calculatedCharges = calculateExcessBuiltUpCharges(
        areaOfExcessBuiltUp,
        configRates?.rrLandRate,
        roundedValue

        //numericValue
    );

    formMethods.setValue("chargesForExcessBuiltUpArea", calculatedCharges);
    recalculateTotal({
      ...values,
      percentRRLandRateForExcessBuiltUpArea: numericValue,
      chargesForExcessBuiltUpArea: calculatedCharges,
      areaOfExcessBuiltUp: numericValue,
    });
  };

  // 3. Handler for Supervision Charges for Open Plot
  const onChangeSupervisionChargesForOpenPlot = (
      event: React.ChangeEvent<any>
  ) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    const values = formMethods.getValues();
    // formMethods.setValue("supervisionChargesForOpenPlot", numericValue);
    recalculateTotal({
      ...values,
      supervisionChargesForOpenPlot: numericValue,
    });
  };

  // 4. Handler for Supervision Charges for Construction
  const onChangeSupervisionChargesForConstruction = (
      event: React.ChangeEvent<any>
  ) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    const values = formMethods.getValues();
    // formMethods.setValue("supervisionChargesForConstruction", numericValue);
    recalculateTotal({
      ...values,
      supervisionChargesForConstruction: numericValue,
    });
  };

  // 5. Handler for Area of Plot (different from existing area field)
  const onChangeAreaOfPlot = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    const numericValue = Number(value.replace(/[^\d.]/g, ""));
    // formMethods.setValue("areaOfPlot", numericValue);
    const values = formMethods.getValues();

    // Recalculate dependent charges
    recalculateDependentCharges({
      ...values,
      areaOfPlot: numericValue,
    });
  };

  // 2. Add new onChange handler for Built-up Area
  const onChangeConstructionArea = (event: React.ChangeEvent<any>) => {
    const formData = formMethods?.getValues() ?? {};
    const value = event.target.value.trim();
    const numericValue = Number(value.replace(/[^\d.]/g, ""));

    // Update the constructionArea value
    formMethods.setValue("constructionArea", numericValue);

    // Recalculate Building Development Charges if rate exists
    if (formData.buildingDevelopmentRRLandRate) {
      const buildingDevelopmentTotal = calculateBuildingDevelopmentCharges(
          numericValue, // Use the new constructionArea value
          configRates?.rrLandRate,
          Number(formData.buildingDevelopmentRRLandRate)
      );
      formMethods.setValue(
          "buildingDevelopmentRRLandTotal",
          buildingDevelopmentTotal
      );
    }

    // Recalculate Labour Cess Charges if rate exists
    if (formData.labourCessRRConstructionRate) {
      const labourCessTotal = calculateLabourCessCharges(
          numericValue, // Use the new constructionArea value
          configRates?.rrConstructionRate,
          Number(formData.labourCessRRConstructionRate)
      );
      formMethods.setValue("labourCessRRConstructionTotal", labourCessTotal);
    }

    // Recalculate total with updated values
    setTimeout(() => {
      const updatedValues = formMethods.getValues();
      const totalAmount = Number(
          (
              Number(updatedValues.particular2Total || 0) +
              Number(updatedValues.particular1Total || 0) +
              Number(updatedValues.buildingDevelopmentRRLandTotal || 0) +
              Number(updatedValues.labourCessRRConstructionTotal || 0) +
              Number(
                  updatedValues.chargesForConstructioInSideMargineRRLandTotal || 0
              ) +
              Number(updatedValues.chargesForExcessBuiltUpArea || 0) +
              Number(updatedValues.rainWaterHarwesting || 0) +
              Number(updatedValues.securityDeposite || 0) +
              Number(updatedValues.supervisionChargesForOpenPlot || 0) +
              Number(updatedValues.supervisionChargesForConstruction || 0)
          ).toFixed(2)
      );

      formMethods.setValue("totalAmount", totalAmount);
    }, 0);
  };

  // 6. Handler for Area of Demolish
  const onChangeAreaOfDemolish = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    const numericValue = Number(value.replace(/[^\d.]/g, ""));
    //formMethods.setValue("areaOfDemolish", numericValue);
    const values = formMethods.getValues();

    // Recalculate charges for construction inside margin
    const percentRate = Number(
        formMethods.getValues().chargesForConstructioInSideMargineRRLandRate || 0
    );
    const calculatedCharges = calculateConstructionInsideMarginCharges(
        numericValue,
        configRates?.rrLandRate,
        percentRate
    );

    formMethods.setValue(
        "chargesForConstructioInSideMargineRRLandTotal",
        calculatedCharges
    );
    recalculateTotal({
      ...values,
      areaOfDemolish: numericValue,
      chargesForConstructioInSideMargineRRLandRate: percentRate,
      chargesForConstructioInSideMargineRRLandTotal: calculatedCharges,
    });
  };

  // 7. Handler for Area of excess built up
  const onChangeAreaOfExcessBuiltUp = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    const numericValue = Number(value.replace(/[^\d.]/g, ""));
    const values = formMethods.getValues();

    //formMethods.setValue("areaOfExcessBuiltUp", numericValue);

    // Recalculate charges for excess built up area
    const percentRate = Number(
        formMethods.getValues().percentRRLandRateForExcessBuiltUpArea || 0
    );
    const calculatedCharges = calculateExcessBuiltUpCharges(
        numericValue,
        configRates?.rrLandRate,
        percentRate
    );

    formMethods.setValue("chargesForExcessBuiltUpArea", calculatedCharges);
    recalculateTotal({
      ...values,
      areaOfExcessBuiltUp: numericValue,
      percentRRLandRateForExcessBuiltUpArea: percentRate,
      chargesForExcessBuiltUpArea: calculatedCharges,
    });
  };

  // 8. Handler for Excess Built Up Area (template field)
  const onChangeExcessBuiltUpArea = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    // formMethods.setValue("excessBuiltUpArea", numericValue);
    // This field appears in template, no calculation needed
  };

  // Add these calculation helper functions (similar to your Site Plan)
  function calculateRegularizationAmount(
      areaOfPlot: number,
      rrLandRate: number,
      percentageRate: number
  ): number {
    const calculatedRate = (percentageRate / 100) * rrLandRate;
    const totalAmount = areaOfPlot * calculatedRate * 3;
    return Number(totalAmount.toFixed(2));
  }

  function calculateDevelopmentAmount(
      areaOfPlot: number,
      rrLandRate: number,
      percentageRate: number
  ): number {
    const calculatedRate = (percentageRate / 100) * rrLandRate;
    const totalAmount = areaOfPlot * calculatedRate;
    return Number(totalAmount.toFixed(2));
  }

  function calculateBuildingDevelopmentCharges(
      constructionArea: number,
      rrLandRate: number,
      percentageRate: number
  ): number {
    const calculatedRate = (percentageRate / 100) * rrLandRate;
    const totalAmount = constructionArea * calculatedRate;
    return Number(totalAmount.toFixed(2));
  }

  function calculateLabourCessCharges(
      constructionArea: number,
      rrConstructionRate: number,
      percentageRate: number
  ): number {
    const calculatedRate = (percentageRate / 100) * rrConstructionRate;
    const totalAmount = calculatedRate * constructionArea;
    return Number(totalAmount.toFixed(2));
  }

  function calculateConstructionInsideMarginCharges(
      areaOfDemolish: number,
      rrLandRate: number,
      percentageRate: number
  ): number {
    const calculatedRate = (percentageRate / 100) * rrLandRate;
    const totalAmount = calculatedRate * areaOfDemolish;
    return Number(totalAmount.toFixed(2));
  }

  function calculateExcessBuiltUpCharges(
      areaOfExcessBuiltUp: number,
      rrLandRate: number,
      percentageRate: number
  ): number {
    const calculatedRate = (percentageRate / 100) * rrLandRate;
    const totalAmount = calculatedRate * areaOfExcessBuiltUp;
    return Number(totalAmount.toFixed(2));
  }

  // Update your recalculateTotal function
  const recalculateTotal = (values: any) => {
    // const values = formMethods.getValues();
    console.log("Values used in calculation:", values);
    console.log("=== CALCULATION DEBUG ===");
    console.log("particular2Total:", Number(values.particular2Total || 0));
    console.log("particular1Total:", Number(values.particular1Total || 0));
    console.log(
        "buildingDevelopmentRRLandTotal:",
        Number(values.buildingDevelopmentRRLandTotal || 0)
    );
    console.log(
        "labourCessRRConstructionTotal:",
        Number(values.labourCessRRConstructionTotal || 0)
    );
    console.log(
        "chargesForConstructioInSideMargineRRLandTotal:",
        Number(values.chargesForConstructioInSideMargineRRLandTotal || 0)
    );
    console.log(
        "chargesForExcessBuiltUpArea:",
        Number(values.chargesForExcessBuiltUpArea || 0)
    );
    console.log(
        "rainWaterHarwesting:",
        Number(values.rainWaterHarwesting || 0)
    );
    console.log("securityDeposite:", Number(values.securityDeposite || 0));
    console.log(
        "supervisionChargesForOpenPlot:",
        Number(values.supervisionChargesForOpenPlot || 0)
    );
    console.log(
        "supervisionChargesForConstruction:",
        Number(values.supervisionChargesForConstruction || 0)
    );
    const totalAmount =
        Number(values.particular2Total || 0) + // Development charges for residential plot
        Number(values.particular1Total || 0) + // Regularization Charges for residential plot
        Number(values.buildingDevelopmentRRLandTotal || 0) + // Building Development Charges
        Number(values.labourCessRRConstructionTotal || 0) + // Labour cess Charges
        Number(values.chargesForConstructioInSideMargineRRLandTotal || 0) + // Charges for construction inside margin
        Number(values.chargesForExcessBuiltUpArea || 0) + // Charges for Excess Built Up Area
        Number(values.rainWaterHarwesting || 0) + // Rainwater Harvesting
        Number(values.securityDeposite || 0) + // Security Deposit
        Number(values.supervisionChargesForOpenPlot || 0) + // Supervision Charges for Open Plot
        Number(values.supervisionChargesForConstruction || 0); // Supervision Charges for Construction

    //formMethods.setValue("totalAmount", totalAmount);
    console.log("total amount", Number(totalAmount.toFixed(2)));
    formMethods.setValue("totalAmount", Number(totalAmount.toFixed(2)));
  };

  // Add this function to recalculate all dependent charges
  const recalculateDependentCharges = (values: any) => {
    // const values = formMethods.getValues();
    const areaOfPlot = Number(values.areaOfPlot || 0);
    const constructionArea = Number(values.constructionArea || 0);

    // Recalculate all charges based on new area values
    if (values.particular1Rate) {
      const regularizationTotal = calculateRegularizationAmount(
          areaOfPlot, // Use areaOfPlot instead of area
          configRates?.rrLandRate,
          Number(values.particular1Rate)
      );
      formMethods.setValue("particular1Total", regularizationTotal);
      values.particular1Total = regularizationTotal;
    }

    if (values.particular2Rate) {
      const developmentTotal = calculateDevelopmentAmount(
          areaOfPlot, // Use areaOfPlot instead of area
          configRates?.rrLandRate,
          Number(values.particular2Rate)
      );
      formMethods.setValue("particular2Total", developmentTotal);
      values.particular2Total = developmentTotal;
    }

    if (values.buildingDevelopmentRRLandRate) {
      const buildingDevTotal = calculateBuildingDevelopmentCharges(
          constructionArea,
          configRates?.rrLandRate,
          Number(values.buildingDevelopmentRRLandRate)
      );
      formMethods.setValue("buildingDevelopmentRRLandTotal", buildingDevTotal);
      values.buildingDevelopmentRRLandTotal = buildingDevTotal;
    }

    if (values.labourCessRRConstructionRate) {
      const labourCessTotal = calculateLabourCessCharges(
          constructionArea,
          configRates?.rrConstructionRate, // Use rrConstructionRate here
          Number(values.labourCessRRConstructionRate)
      );
      formMethods.setValue("labourCessRRConstructionTotal", labourCessTotal);
      values.labourCessRRConstructionTotal = labourCessTotal;
    }

    if (
        values.chargesForConstructioInSideMargineRRLandRate &&
        values.areaOfDemolish
    ) {
      const constructionMarginTotal = calculateConstructionInsideMarginCharges(
          Number(values.areaOfDemolish),
          configRates?.rrLandRate,
          Number(values.chargesForConstructioInSideMargineRRLandRate)
      );
      formMethods.setValue(
          "chargesForConstructioInSideMargineRRLandTotal",
          constructionMarginTotal
      );
      values.chargesForConstructioInSideMargineRRLandTotal =
          constructionMarginTotal;
    }

    if (
        values.percentRRLandRateForExcessBuiltUpArea &&
        values.areaOfExcessBuiltUp
    ) {
      const excessBuiltUpTotal = calculateExcessBuiltUpCharges(
          Number(values.areaOfExcessBuiltUp),
          configRates?.rrLandRate,
          Number(values.percentRRLandRateForExcessBuiltUpArea)
      );
      formMethods.setValue("chargesForExcessBuiltUpArea", excessBuiltUpTotal);
      values.chargesForExcessBuiltUpArea = excessBuiltUpTotal;
    }

    recalculateTotal(values);
  };

  const onChangeChargesForConstructioInSideMargineRRLandRate = (
      event: React.ChangeEvent<any>
  ) => {
    const value = event.target.value.trim();
    //const numericValue = Number(value.replace(/[^\d.]/g, ""));
    const numericValue = value.replace(/[^\d.]/g, "");
    const roundedValue =
        Math.round(parseFloat(numericValue || "0") * 100) / 100;

    const values = formMethods.getValues();

    const areaOfDemolish = Number(formMethods.getValues().areaOfDemolish || 0);
    const constructionMarginTotal = calculateConstructionInsideMarginCharges(
        areaOfDemolish,
        configRates?.rrLandRate,
        //numericValue
        roundedValue
    );

    formMethods.setValue(
        "chargesForConstructioInSideMargineRRLandTotal",
        constructionMarginTotal
    );
    recalculateTotal({
      ...values,
      chargesForConstructioInSideMargineRRLandRate: numericValue,
      buildingDevelopmentRRLandTotal: constructionMarginTotal,
      areaOfDemolish: numericValue,
    });
  };

  const onChangeChargesForConstructioInSideMargineRRLandTotal = (
      event: React.ChangeEvent<any>
  ) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    formMethods.setValue(
        "chargesForConstructioInSideMargineRRLandTotal",
        numericValue
    );
    //recalculateTotal();
  };

  const onChangeRainWaterHarwesting = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    const values = formMethods.getValues();
    // formMethods.setValue("rainWaterHarwesting", numericValue);
    recalculateTotal({
      ...values,
      rainWaterHarwesting: numericValue,
    });
  };

  const onChangeSecurityDeposite = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    const numericValue = value.replace(/[^\d.]/g, "");
    const values = formMethods.getValues();

    recalculateTotal({
      ...values,
      securityDeposite: numericValue,
    });
  };

  const onChangeTotalAmount = (event: React.ChangeEvent<any>) => {
    const value = event.target.value.trim();
    // Remove non-numeric characters from the input value
    const numericValue = value.replace(/[^\d.]/g, "");
    const roundedValue = parseFloat(numericValue || "0");
    console.log("total amoount coming", numericValue);
    // formMethods.setValue("totalAmount", numericValue);

    formMethods.setValue("totalAmount", Number(roundedValue.toFixed(2)));
  };

  const [isEditableConditions, setIsEditableConditions] = useState(false);
  const toggleEditableConditions = () => {
    setIsEditableConditions((prev) => !prev);
  };

  return (
      <FormProvider {...formMethods}>
        <h4
            className={`text-center text-grey-600 font-semibold mb-8 text-2xl ${pdfToView ? "hidden" : ""
            }`}
        >
          Demand Note - Building Plan
        </h4>

        <form
            id="create-demand-note"
            className={`grid grid-cols-2 gap-4 ${pdfToView ? "hidden" : ""}`}
            noValidate
            onSubmit={formMethods.handleSubmit((data) =>
                saveDataMutation.mutate(
                    {
                      applicationId: application.id,
                      reviewId,
                      data,
                      isThereAConst: isThereAConst ?? false,
                    },
                    {
                      onSuccess: (result) => {
                        const blob = new Blob([result.data], { type: "application/pdf" });
                        const url = URL.createObjectURL(blob);
                        setPdfToView(url);
                      },
                    }
                )
            )}
        >
          <div className="col-span-2 md:col-span-1">
            <Input
                disabled
                label="Applicant Name"
                placeholder="Applicant Name"
                {...formMethods.register("applicantName")}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                disabled
                label="Name of Layout to be regularized"
                placeholder="Name of Layout to be regularized"
                {...formMethods.register("layoutName")}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                disabled
                label="Mauza"
                placeholder="Mauza"
                {...formMethods.register("mauja")}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                disabled
                label="Khasara No"
                placeholder="Khasara No"
                {...formMethods.register("khasaraNo")}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-end justify-between gap-4">
              <Input
                  disabled
                  type="number"
                  onWheel={(e) => {
                    e.currentTarget.blur();

                    e.stopPropagation();

                    setTimeout(() => {
                      e.currentTarget.focus();
                    }, 0);
                  }}
                  containerClass="basis-3/5 md:basis-3/4"
                  label="Plot Area"
                  placeholder="Plot Area"
                  {...formMethods.register("area", { valueAsNumber: true })}
              />
              {/*
             <Select
              containerClass="basis-2/5 md:basis-1/4"
              disabled
              {...formMethods.register("unit_area")}
            >
              <option hidden></option>
              <option value="sq. mt.">sq. mt.</option>
              <option value="sq. ft.">sq. ft.</option>
            </Select>
            */}
              <Input
                  containerClass="basis-2/5 md:basis-1/4"
                  value="sq. mt."
                  disabled={true}
                  readOnly={true}
                  {...formMethods.register("unit_area", { value: "sq. mt." })}
              />
            </div>
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                  e.stopPropagation();
                  setTimeout(() => {
                    e.currentTarget.focus();
                  }, 0);
                }}
                label="Built-up Area [sq. mt.]"
                placeholder="Built-up Area [sq. mt.]"
                error={!!formState.errors?.constructionArea}
                helperText={formState?.errors?.constructionArea?.message?.toString()}
                {...formMethods.register("constructionArea", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                onChange={onChangeConstructionArea}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                disabled
                label="Plot No"
                placeholder="Plot No"
                {...formMethods.register("plotNo")}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                type="date"
                label="Due Date"
                placeholder="Due Date"
                {...formMethods.register("dueDate", { valueAsDate: true })}
            />
          </div>

          {/* <div className="col-span-2 md:col-span-1">
          <Input
            required
            type="number"
            label="Charges for Excess Built Up Area"
            placeholder="Charges for Excess Built Up Area"
            error={!!formState.errors?.chargesForExcessBuiltUpArea}
            helperText={formState?.errors?.chargesForExcessBuiltUpArea?.message?.toString()}
            {...formMethods.register("chargesForExcessBuiltUpArea", {
              required: "* Required",
              valueAsNumber: true,
            })}
            onChange={onChangeChargesForExcessBuiltUpArea}
          />
        </div> */}

          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                label="Area of Plot"
                placeholder="Area of Plot"
                error={!!formState.errors?.areaOfPlot}
                helperText={formState?.errors?.areaOfPlot?.message?.toString()}
                {...formMethods.register("areaOfPlot", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                onChange={onChangeAreaOfPlot}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                label="Area of Demolish"
                placeholder="Area of Demolish"
                error={!!formState.errors?.areaOfDemolish}
                helperText={formState?.errors?.areaOfDemolish?.message?.toString()}
                {...formMethods.register("areaOfDemolish", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                onChange={onChangeAreaOfDemolish}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                label="Area of excess built up"
                placeholder="Area of excess built up"
                error={!!formState.errors?.areaOfExcessBuiltUp}
                helperText={formState?.errors?.areaOfExcessBuiltUp?.message?.toString()}
                {...formMethods.register("areaOfExcessBuiltUp", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                onChange={onChangeAreaOfExcessBuiltUp}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                label="Excess Built Up Area"
                placeholder="Excess Built Up Area"
                error={!!formState.errors?.excessBuiltUpArea}
                helperText={formState?.errors?.excessBuiltUpArea?.message?.toString()}
                {...formMethods.register("excessBuiltUpArea", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                onChange={onChangeExcessBuiltUpArea}
            />
          </div>

          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();

                  e.stopPropagation();

                  setTimeout(() => {
                    e.currentTarget.focus();
                  }, 0);
                }}
                label="% of R.R Land Rate for Regularization Charges"
                placeholder="% of R.R Land Rate for Regularization Charges"
                error={!!formState.errors?.particular1Rate}
                helperText={formState?.errors?.particular1Rate?.message?.toString()}
                {...formMethods.register("particular1Rate", {
                  required: "* Required",
                  valueAsNumber: true,
                  validate: (val: any) =>
                      /^\d+(\.\d{1,2})?$/.test(val) || "Max 2 decimal places allowed",
                })}
                onChange={onChangeParticular1Rate}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1 bold">
              {/* <b>Regularization charges for residential plot</b> */}
              <b>Regularization charges for residential plot Total</b>
            </span>

              <i
                  className={`fa ${isEditable.particular1Total ? "fa-lock" : "fa-pencil"
                  } cursor-pointer`}
                  onClick={() => toggleEditable("particular1Total")}
              ></i>
            </label>
            <Input
                required
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();

                  e.stopPropagation();

                  setTimeout(() => {
                    e.currentTarget.focus();
                  }, 0);
                }}
                label=""
                placeholder="Regularization charges for residential plot Total"
                error={!!formState.errors?.particular1Total}
                helperText={formState?.errors?.particular1Total?.message?.toString()}
                {...formMethods.register("particular1Total", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                // onChange={(e) => onChangeHandler('particular1Total', e)}
                onChange={onChangeParticular1Total}
                disabled={!isEditable.particular1Total}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();

                  e.stopPropagation();

                  setTimeout(() => {
                    e.currentTarget.focus();
                  }, 0);
                }}
                label="% of R.R Land Rate for Development Charges"
                placeholder="“% of R.R Land Rate for Development Charges"
                error={!!formState.errors?.particular2Rate}
                helperText={formState?.errors?.particular2Rate?.message?.toString()}
                {...formMethods.register("particular2Rate", {
                  required: "* Required",
                  valueAsNumber: true,
                  validate: (val: any) =>
                      /^\d+(\.\d{1,2})?$/.test(val) || "Max 2 decimal places allowed",
                })}
                onChange={onChangeParticular2Rate}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1">
              {/* <b>Development charges for residential plot</b>{" "} */}
              <b>Development charges for residential plot Total</b>{" "}
            </span>
              <i
                  className={`fa ${isEditable.particular2Total ? "fa-lock" : "fa-pencil"
                  } cursor-pointer`}
                  onClick={() => toggleEditable("particular2Total")}
              ></i>
            </label>

            <Input
                required
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                  e.stopPropagation();
                  setTimeout(() => {
                    e.currentTarget.focus();
                  }, 0);
                }}
                label=""
                placeholder="Development charges for residential plot Total"
                error={!!formState.errors?.particular2Total}
                helperText={formState?.errors?.particular2Total?.message?.toString()}
                {...formMethods.register("particular2Total", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                // onChange={(e) => onChangeHandler('particular2Total', e)}
                onChange={onChangeParticular2Total}
                disabled={!isEditable.particular2Total}
            />
          </div>
          {/* <div className="col-span-2 md:col-span-1">
          <Input
            required
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();

              e.stopPropagation();

              setTimeout(() => {
                e.currentTarget.focus();
              }, 0);
            }}
            label="Scrutiny Charges"
            placeholder="Scrutiny Charges"
            error={!!formState.errors?.scrutinyCharges}
            helperText={formState?.errors?.scrutinyCharges?.message?.toString()}
            {...formMethods.register("scrutinyCharges", {
              required: "* Required",
              valueAsNumber: true,
            })}
            onChange={onChangeScrutinyCharges}
          />
        </div> */}
          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                label="% of R.R Land Rate for Building Development Charges"
                placeholder="% of R.R Land Rate for Building Development Charges"
                error={!!formState.errors?.buildingDevelopmentRRLandRate}
                helperText={formState?.errors?.buildingDevelopmentRRLandRate?.message?.toString()}
                {...formMethods.register("buildingDevelopmentRRLandRate", {
                  required: "* Required",
                  valueAsNumber: true,
                  validate: (val: any) =>
                      /^\d+(\.\d{1,2})?$/.test(val) || "Max 2 decimal places allowed",
                })}
                onChange={onChangeBuildingDevelopmentRRLandRate}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1 bold">
              {/* <b>Building Development RR Land Total</b> */}
              <b>Building Development Charges</b>
            </span>
              <i
                  className={`fa ${isEditable.buildingDevelopmentRRLandTotal
                      ? "fa-lock"
                      : "fa-pencil"
                  } cursor-pointer`}
                  onClick={() => toggleEditable("buildingDevelopmentRRLandTotal")}
              ></i>
            </label>
            <Input
                required
                type="number"
                placeholder="Building Development Charges"
                error={!!formState.errors?.buildingDevelopmentRRLandTotal}
                helperText={formState?.errors?.buildingDevelopmentRRLandTotal?.message?.toString()}
                {...formMethods.register("buildingDevelopmentRRLandTotal", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                onChange={(e) => {
                  const value = e.target.value.trim();
                  const numericValue = value.replace(/[^\d.]/g, "");
                  // formMethods.setValue("securityDeposite", numericValue);
                  const values = formMethods.getValues();

                  recalculateTotal({
                    ...values,
                    buildingDevelopmentRRLandTotal: numericValue,
                  });
                }}
                disabled={!isEditable.buildingDevelopmentRRLandTotal}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                label="% of R.R Construction Rate for Labour Cess"
                placeholder="% of R.R Construction Rate for Labour Cess"
                error={!!formState.errors?.labourCessRRConstructionRate}
                helperText={formState?.errors?.labourCessRRConstructionRate?.message?.toString()}
                {...formMethods.register("labourCessRRConstructionRate", {
                  required: "* Required",
                  valueAsNumber: true,
                  validate: (val: any) =>
                      /^\d+(\.\d{1,2})?$/.test(val) || "Max 2 decimal places allowed",
                })}
                onChange={onChangeLabourCessRRConstructionRate}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1 bold">
              <b>Labour Cess Charges</b>
            </span>
              <i
                  className={`fa ${isEditable.labourCessRRConstructionTotal
                      ? "fa-lock"
                      : "fa-pencil"
                  } cursor-pointer`}
                  onClick={() => toggleEditable("labourCessRRConstructionTotal")}
              ></i>
            </label>
            <Input
                required
                type="number"
                placeholder="Labour Cess Charges"
                error={!!formState.errors?.labourCessRRConstructionTotal}
                helperText={formState?.errors?.labourCessRRConstructionTotal?.message?.toString()}
                {...formMethods.register("labourCessRRConstructionTotal", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                onChange={(e) => {
                  const value = e.target.value.trim();
                  const numericValue = value.replace(/[^\d.]/g, "");
                  // formMethods.setValue("securityDeposite", numericValue);
                  const values = formMethods.getValues();

                  recalculateTotal({
                    ...values,
                    labourCessRRConstructionTotal: numericValue,
                  });
                }}
                disabled={!isEditable.labourCessRRConstructionTotal}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                label="% of R.R Land Rate for Construction inside margin"
                placeholder="% of R.R Land Rate for Construction inside margin"
                error={
                  !!formState.errors?.chargesForConstructioInSideMargineRRLandRate
                }
                helperText={formState?.errors?.chargesForConstructioInSideMargineRRLandRate?.message?.toString()}
                {...formMethods.register(
                    "chargesForConstructioInSideMargineRRLandRate",
                    {
                      required: "* Required",
                      valueAsNumber: true,
                      validate: (val: any) =>
                          /^\d+(\.\d{1,2})?$/.test(val) ||
                          "Max 2 decimal places allowed",
                    }
                )}
                onChange={onChangeChargesForConstructioInSideMargineRRLandRate}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1 bold">
              <b>Charges for Construction inside margin</b>
            </span>
              <i
                  className={`fa ${isEditable.chargesForConstructioInSideMargineRRLandTotal
                      ? "fa-lock"
                      : "fa-pencil"
                  } cursor-pointer`}
                  onClick={() =>
                      toggleEditable("chargesForConstructioInSideMargineRRLandTotal")
                  }
              ></i>
            </label>
            <Input
                required
                type="number"
                placeholder="Charges for Construction inside margin"
                error={
                  !!formState.errors?.chargesForConstructioInSideMargineRRLandTotal
                }
                helperText={formState?.errors?.chargesForConstructioInSideMargineRRLandTotal?.message?.toString()}
                {...formMethods.register(
                    "chargesForConstructioInSideMargineRRLandTotal",
                    {
                      required: "* Required",
                      valueAsNumber: true,
                    }
                )}
                onChange={(e) => {
                  const value = e.target.value.trim();
                  const numericValue = value.replace(/[^\d.]/g, "");
                  // formMethods.setValue("securityDeposite", numericValue);
                  const values = formMethods.getValues();

                  recalculateTotal({
                    ...values,
                    chargesForConstructioInSideMargineRRLandTotal: numericValue,
                  });
                }}
                disabled={!isEditable.chargesForConstructioInSideMargineRRLandTotal}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                label="% of R.R Land Rate for Excess Built Up Area"
                placeholder="% of R.R Land Rate for Excess Built Up Area"
                error={!!formState.errors?.percentRRLandRateForExcessBuiltUpArea}
                helperText={formState?.errors?.percentRRLandRateForExcessBuiltUpArea?.message?.toString()}
                {...formMethods.register("percentRRLandRateForExcessBuiltUpArea", {
                  required: "* Required",
                  valueAsNumber: true,
                  validate: (val: any) =>
                      /^\d+(\.\d{1,2})?$/.test(val) || "Max 2 decimal places allowed",
                })}
                onChange={onChangePercentRRLandRateForExcessBuiltUpArea}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1 bold">
              <b>Charges for Excess Built Up Area</b>
            </span>

              <i
                  className={`fa ${isEditable.chargesForExcessBuiltUpArea ? "fa-lock" : "fa-pencil"
                  } cursor-pointer`}
                  onClick={() => toggleEditable("chargesForExcessBuiltUpArea")}
              ></i>
            </label>
            <Input
                required
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                  e.stopPropagation();
                  setTimeout(() => {
                    e.currentTarget.focus();
                  }, 0);
                }}
                label=""
                placeholder="Charges for Excess Built Up Area"
                error={!!formState.errors?.chargesForExcessBuiltUpArea}
                helperText={formState?.errors?.chargesForExcessBuiltUpArea?.message?.toString()}
                {...formMethods.register("chargesForExcessBuiltUpArea", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                onChange={onChangeChargesForExcessBuiltUpArea} // keeps your calculation logic intact
                disabled={!isEditable.chargesForExcessBuiltUpArea}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                label="Supervision Charges for Open Plot"
                placeholder="Supervision Charges for Open Plot"
                error={!!formState.errors?.supervisionChargesForOpenPlot}
                helperText={formState?.errors?.supervisionChargesForOpenPlot?.message?.toString()}
                {...formMethods.register("supervisionChargesForOpenPlot", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                onChange={onChangeSupervisionChargesForOpenPlot}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                label="Supervision Charges for Construction"
                placeholder="Supervision Charges for Construction"
                error={!!formState.errors?.supervisionChargesForConstruction}
                helperText={formState?.errors?.supervisionChargesForConstruction?.message?.toString()}
                {...formMethods.register("supervisionChargesForConstruction", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                onChange={onChangeSupervisionChargesForConstruction}
            />
          </div>
          {/* <div className="col-span-2 md:col-span-1">
          <Input
            required
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();

              e.stopPropagation();

              setTimeout(() => {
                e.currentTarget.focus();
              }, 0);
            }}
            label="Area Excess Of Basic FSI"
            placeholder="Area Excess Of Basic FSI"
            error={!!formState.errors?.areaExcessOfBasicFSI}
            helperText={formState?.errors?.areaExcessOfBasicFSI?.message?.toString()}
            {...formMethods.register("areaExcessOfBasicFSI", {
              required: "* Required",
              valueAsNumber: true,
            })}
            onChange={onChangeAreaExcessOfBasicFSI}
          />
        </div> */}
          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();

                  e.stopPropagation();

                  setTimeout(() => {
                    e.currentTarget.focus();
                  }, 0);
                }}
                label="Rain Water Harvesting"
                placeholder="Rain Water Harvesting"
                error={!!formState.errors?.rainWaterHarwesting}
                helperText={formState?.errors?.rainWaterHarwesting?.message?.toString()}
                {...formMethods.register("rainWaterHarwesting", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                onChange={onChangeRainWaterHarwesting}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                required
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();

                  e.stopPropagation();

                  setTimeout(() => {
                    e.currentTarget.focus();
                  }, 0);
                }}
                label="Security Deposit"
                placeholder="Security Deposit"
                error={!!formState.errors?.securityDeposite}
                helperText={formState?.errors?.securityDeposite?.message?.toString()}
                {...formMethods.register("securityDeposite", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                onChange={onChangeSecurityDeposite}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="flex items-center">
            <span className="mr-2 p-1">
              <b> Total Demand Note Amount</b>{" "}
            </span>
              <i
                  className={`fa ${isEditable.totalAmount ? "fa-lock" : "fa-pencil"
                  } m-1 cursor-pointer`}
                  onClick={() => toggleEditable("totalAmount")}
              ></i>
            </label>
            <Input
                required
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();

                  e.stopPropagation();

                  setTimeout(() => {
                    e.currentTarget.focus();
                  }, 0);
                }}
                label=""
                placeholder="Total Demand Note Amount"
                error={!!formState.errors?.totalAmount}
                helperText={formState?.errors?.totalAmount?.message?.toString()}
                {...formMethods.register("totalAmount", {
                  required: "* Required",
                  valueAsNumber: true,
                })}
                // onChange={(e) => onChangeHandler('totalAmount', e)}
                onChange={onChangeTotalAmount}
                disabled={!isEditable.totalAmount}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                disabled
                label="Municipal Council Name"
                placeholder="Municipal Council Name"
                error={!!formState.errors?.municipality}
                helperText={formState?.errors?.municipality?.message?.toString()}
                {...formMethods.register("municipality", {
                  required: "* Required",
                })}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                disabled
                label="Office Address: Tahasil"
                placeholder="Office Address: Tahasil"
                error={!!formState.errors?.tehsil}
                helperText={formState?.errors?.tehsil?.message?.toString()}
                {...formMethods.register("tehsil", { required: "* Required" })}
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
                disabled
                label="Office Address: District"
                placeholder="Office Address: District"
                error={!!formState.errors?.district}
                helperText={formState?.errors?.district?.message?.toString()}
                {...formMethods.register("district", { required: "* Required" })}
            />
          </div>
          <div className="col-span-2">
            <label className="flex items-center">
            <span className="text-base font-bold mb-2">
              Conditions For Demand Note
            </span>
              <i
                  className={`fa ${isEditableConditions ? "fa-lock" : "fa-pencil"
                  } cursor-pointer ml-2`}
                  onClick={toggleEditableConditions}
              ></i>
            </label>
            <textarea
                placeholder="Conditions for Demand Note"
                {...formMethods.register("conditions")}
                rows={5}
                className={`h-auto w-100 ${isEditableConditions ? "bg-white" : "bg-gray-200"
                }`}
                disabled={!isEditableConditions}
            />
          </div>
          <div className="col-span-2 flex justify-center space-x-4 items-center mt-4">
            <button
                type="button"
                onClick={onBack}
                disabled={saveDataMutation.isPending || forwardMutation.isPending}
                className="bg-black px-4 md:px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              Back
            </button>
            <button
                form="create-demand-note"
                type="submit"
                disabled={saveDataMutation.isPending || forwardMutation.isPending}
                className="bg-primary px-4 md:px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              {"Save And Preview"}
            </button>
          </div>
        </form>
        {!!pdfToView && (
            <>
              <iframe
                  title="PDF Viewer"
                  // className="w-full h-full min-h-screen hidden sm:block"
                  className={style.IFrameCss}
                  src={pdfToView + "#toolbar=0"}
              />
              <div className="hidden">
                <p>Demand Note</p>
                <a
                    className="inline-block text-white py-2 px-4 rounded-full bg-blue-500"
                    download={`${application.applicationId}-Demand-Note.pdf`}
                    href={pdfToView}
                >
                  Download
                </a>
              </div>
              <div className="col-span-2 flex justify-center space-x-4 items-center mt-8">
                <button
                    type="button"
                    onClick={() => setPdfToView(null)}
                    disabled={saveDataMutation.isPending || forwardMutation.isPending}
                    className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                >
                  Back
                </button>
                <button
                    form="create-demand-note"
                    type="submit"
                    onClick={(data) =>
                        forwardMutation.mutate(
                            {
                              applicationId: application.id,
                              reviewId,
                              isThereAConst: isThereAConst ?? false,
                            },
                            { onSuccess: () => navigate("/level-1-applications") }
                        )
                    }
                    disabled={saveDataMutation.isPending || forwardMutation.isPending}
                    className="bg-primary px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
                >
                  {forwardMutation.isPending ? "Forwarding" : "Forward"}
                </button>
              </div>
            </>
        )}
      </FormProvider>
  );
}


type ReviewProps = {
  steps?: TPopulatedForm[];
  onBack: () => unknown;
  onNext: () => unknown;
  application: TApplication & Record<string, unknown>;
  allDocsApproved?: boolean;
};

const NON_SELECTABLE_FIELDS = [
  "noc_number",
];

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const simpleDownload = (url: string) => {
  window.open(url, '_blank');
};

function Review({
  allDocsApproved,
  steps,
  onBack,
  onNext,
  application,
}: ReviewProps) {
  const qc = useQueryClient()
  const navigate = useNavigate();
  const [reviewsDialog, toggleReviewsDialog] = useToggle();
  const allReviews = useLevel1AllReviews({ id: application.id });
  const saveReviewMutation = useSaveLevel1Review();
  const lastReviewQuery = useLevel1LastReview({ id: application.id });
  const [fieldsToChange, setFieldsToChange] = useState<number[]>([]);
  const [comment, setComment] = useState("");
  const returnMutation = useLevel1ReturnApplication();
  const forwardMutation = useForwardApplicationForLevel1();

  const disabled =
    lastReviewQuery.isFetching ||
    saveReviewMutation.isPending ||
    returnMutation.isPending ||
    application.status !== "Pending Level 1 Review";

  const lastReviewData = lastReviewQuery.data;

  const allFields = useMemo(
    () =>
      (steps || [])
        .flatMap((e) => e.fields || [])
        .filter((e) => e?.active && !NON_SELECTABLE_FIELDS.includes(e.uid)),
    [steps]
  );

  const defaultSelected = useMemo(() => {
    return allFields.filter((e) =>
      lastReviewData?.fieldsToChange?.includes(e.id)
    );
  }, [allFields, lastReviewData]);

  useEffect(() => {
    setComment(lastReviewData?.review?.comment_text || "");
    setFieldsToChange(lastReviewData?.fieldsToChange || []);
  }, [lastReviewData]);

  if (lastReviewQuery.isLoading || lastReviewQuery.isError) {
    return (
      <div className="flex justify-center items-center p-8">
        {lastReviewQuery.isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  let level1ReviewInfoToRender = null;

  if (allReviews.isLoading || allReviews.isError) {
    level1ReviewInfoToRender = (
      <div className="flex justify-center items-center p-8">
        {allReviews.isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  } else {
    const level1Review = (
      allReviews.data || []
    ).reduce<TReviewWithReviewer | null>(
      (acc, e) => (e?.level === "Level 1" ? e : acc),
      null
    );
    if (level1Review) {
      level1ReviewInfoToRender = (
        <div className="mb-8">
          {/* <div className="flex justify-between mb-4 items-center space-x-4">
            <h4 className="text-xl font-bold grow">Previous Reviewer Remark</h4>
            <img src={userImg} height={36} width={36} alt="profile-pic" />
            <div>
              <p className="text-base font-semibold">
                {level1Review.reviewer?.name || "--"}
              </p>
              <p className="text-xs">{formatDate(level1Review.date || "")}</p>
            </div>
          </div>
          <h6 className="mb-2 text-lg font-bold">Level 1 Reviewer Remark</h6>
          <p className="text-base">{level1Review.comment_text}</p> */}
          <h4 className="text-xl font-bold mb-4">Previous Reviewer Remark</h4>
          <div className="flex justify-between  items-center space-x-4">
            <div className="grow">
              <h6 className="mb-2 text-lg font-bold">
                Level 1 Reviewer Remark
              </h6>
              <p className="text-base">{level1Review.comment_text}</p>
            </div>
            <img src={userImg} height={36} width={36} alt="profile-pic" />
            <div>
              <p className="text-base font-semibold">
                {level1Review.reviewer?.name || "--"}
              </p>
              <p className="text-xs">{formatDate(level1Review.date || "")}</p>
            </div>
          </div>
        </div>
      );
    }
  }




  console.log('user id :', application?.userId);

  return (
    <>
      {level1ReviewInfoToRender}
      <div className="grid grid-cols-5 gap-4 items-center">
        <div className="col-span-5 md:col-span-4">
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm md:text-base font-bold">
              Level 1 Reviewer Remark <span className="text-red-500">*</span>
            </label>
            <button
              className="text-sm md:text-base font-bold disabled:text-gray-500"
              disabled={!allReviews.data?.length}
              onClick={toggleReviewsDialog}
            >
              <i className="fa fa-history"></i>
              Previous Reviews
            </button>
          </div>
          <textarea
            placeholder="Add Comment"
            value={comment}
            disabled={disabled}
            rows={5}
            className="h-auto w-100"
            onChange={(e) => setComment(e.target.value)}
          />
          <Multiselect
            disable={disabled}
            key={`multiselect-${lastReviewQuery.isLoading}`}
            style={{
              multiselectContainer: {
                border: "1px solid #dee2e6",
                "border-radius": "8px",
              },
            }}
            selectedValues={defaultSelected}
            placeholder="Select Change Fields"
            options={allFields}
            onSelect={(list: TApplicationField[]) =>
              setFieldsToChange(list.map((e) => e.id))
            }
            onRemove={(list: TApplicationField[]) =>
              setFieldsToChange(list.map((e) => e.id))
            }
            displayValue="label"
          />

          <div className="flex-wrap md:flex-nowrap flex justify-center gap-4 items-center mt-8">
            <button
              type="button"
              onClick={onBack}
              disabled={saveReviewMutation.isPending}
              className="bg-black px-10 py-2 rounded-lg md:rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 basis-full md:basis-auto"
            >
              Back
            </button>
            <button
              type="button"
              disabled={disabled || !comment?.trim()}
              onClick={() => {
                saveReviewMutation.mutate({
                  applicationId: application.id,
                  comment,
                  fieldsToChange,
                });
              }}
              className="bg-primary px-10 py-2 rounded-lg md:rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500 basis-full md:basis-auto"
            >
              {saveReviewMutation.isPending ? "Saving" : "Save"}
            </button>

          </div>
        </div>
        <div className="col-span-5 md:col-span-1 space-y-4">
          <button
            type="button"
            disabled={
              disabled ||
              !lastReviewQuery.data?.review?.id ||
              !allDocsApproved ||
              !!fieldsToChange?.length
            }
            onClick={() => {
              onNext?.();
            }}
            className="bg-primary w-full py-2 rounded-lg font-bold text-white block disabled:bg-gray-300 disabled:text-gray-500"
          >
            Approve & Next
          </button>
          <button
            type="button"
            disabled={
              disabled ||
              !lastReviewQuery.data?.review?.id ||
              (allDocsApproved && !fieldsToChange.length)
            }
            className="bg-red-400 w-full py-2 rounded-lg font-bold text-white block disabled:bg-gray-300 disabled:text-gray-500"
            onClick={() =>
              returnMutation.mutate(
                {
                  applicationId: application.id,
                  application_id: application.applicationId,
                  user_id: application.userId,
                },
                {
                  onSuccess: () =>
                    navigate("/level-1-applications"),
                  onSettled: () => { }
                }
              )
            }
          >
            {returnMutation.isPending ? "Returning" : "Return"}
          </button>

        </div>
      </div>
      {reviewsDialog && (
        <RenderAllReviews
          onClose={toggleReviewsDialog}
          reviews={allReviews.data || []}
        />
      )}
    </>
  );
}

type ViewAndValidateDocumentsAndNOCsProps = {
  nocDecisions?: Record<string, boolean>;
  documentDecisions?: Record<string, boolean>;
  steps?: TPopulatedForm[];
  application: TApplication & Record<string, unknown>;
  onNext?: () => unknown;
};

function ViewAndValidateDocumentsAndNOCs({
  steps,
  application,
  nocDecisions,
  documentDecisions,
  onNext,
}: ViewAndValidateDocumentsAndNOCsProps) {
  const status = application?.status;
  const id = application?.id;
  const [document, setDocument] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const navigate = useNavigate();
  const docDecisionMutation = useLevel1UpdateDocumentDecision();
  const nocDecisionMutation = useLevel1UpdateNocDecision();
  const { isPending: updatingNocDecision, mutate: updateNocDecision } =
    nocDecisionMutation;

  const nocColumns = useMemo((): TableColumn<Record<string, unknown>>[] => {
    const noc = steps?.find((e) => e.type === "NOC");
    if (!noc) return [];
    return [
      { name: "Sr. No.", selector: (_, i) => (i || 0) + 1, center: true, grow: 0.5 },
      {
        name: "NOC Type",
        selector: (d) =>
          String(
            (noc.fields?.find((e) => e.uid === "noc_type")?.options || []).find(
              (e) => e.id === d.noc_type
            )?.name || "-"
          ),
        grow: 3,
        center: true,
      },
      {
        name: "NOC No",
        selector: (d) => String(d?.noc_number || "-"),
        center: true,
        grow: 2,
        width: "120px",
      },
      {
        name: "NOC Date",
        selector: (d) => formatDate(String(d?.noc_date || "x")),
        center: true,
        grow: 2,
        width: "130px",
      },
      // {
      //   name: "Level 1",
      //   center: true,
      //   cell: () => (
      //     <i className="m-0 fa fa text-lg fa-check-square-o text-green-400"></i>
      //   ),
      // },
      {
        name: "View",
        center: true,
        width: "180px",
        cell: (d, i) => (
          <div className="flex space-x-4">
            {/* <button
              className="text-lg"
              disabled={
                updatingNocDecision || status !== "Pending Level 1 Review"
              }
              onClick={() => {
                updateNocDecision({
                  applicationId: id,
                  decision: nocDecisions?.[String(d?.id)] === false ? 1 : 0,
                  nocId: Number(d.id),
                });
              }}
            >
              {nocDecisions?.[String(d?.id)] === false ? (
                <i className="m-0 fa fa-window-close-o text-red-500"></i>
              ) : (
                <i className="m-0 fa fa-check-square-o text-green-400"></i>
              )}
            </button>

            <button
              className="text-lg"
              onClick={() =>
                setDocument({
                  url: String(d?.noc_doc),
                  name: String(
                    noc.fields
                      ?.find((e) => e.uid === "noc_type")
                      ?.options?.find((e) => e.id === d.noc_type)?.name || ""
                  ),
                })
              }
            >
              <i className="m-0 fa fa-eye"></i>
            </button>
            <button
              onClick={() => downloadResource(String(d?.noc_doc))}
              className="text-lg"
            >
              <i className="m-0 fa fa-download"></i>
            </button> */}
            {/* <div className="flex space-x-2"> */}
            {/* <button
              className="text-lg"
              disabled={
                updatingNocDecision || status !== "Pending Level 1 Review"
              }
              onClick={() => {
                updateNocDecision({
                  applicationId: id,
                  decision: nocDecisions?.[String(d?.id)] === false ? 1 : 0,
                  nocId: Number(d.id),
                });
              }}
            >
              {nocDecisions?.[String(d?.id)] === false ? (
                <i className="m-0 fa fa-window-close-o text-red-500 bg-red-200 p-3 rounded-md"></i>
              ) : (
                <i className="m-0 fa fa-check-square-o text-green-400 bg-blue-200 p-3 rounded-md"></i>
              )}
            </button> */}
            <NocL1Button
              isChecked={nocDecisions?.[String(d?.id)] !== false} // Change this line
              isUpdating={updatingNocDecision}
              statusCheck={status}
              onClick={() => {
                updateNocDecision({
                  applicationId: id,
                  decision: nocDecisions?.[String(d?.id)] === false ? 1 : 0,
                  nocId: Number(d.id),
                });
              }}
              tooltipText="Toggle NOC Decision"
            />
            <ViewButton
              // onClick={() =>
              //   setDocument({
              //     url: String(d?.noc_doc),
              //     name: String(
              //       noc.fields
              //         ?.find((e) => e.uid === "noc_type")
              //         ?.options?.find((e) => e.id === d.noc_type)?.name || ""
              //     ),
              //   })
              // }
              // onClick={() => {
              //   openInNewTab(String(d?.url));
              onClick={() => {
              openInNewTab(String(d?.noc_doc));
              
              }}
            />
            <DownloadButton
              onClick={() => downloadResource(String(d?.noc_doc))}
              //onClick={() => simpleDownload(String(d?.url))} 
            />
            {/* </div> */}
          </div>
        ),
      },
    ];
  }, [status, steps, updatingNocDecision, nocDecisions, updateNocDecision]);

  const { isPending: updatingDocDecision, mutate: updateDocDecision } =
    docDecisionMutation;

  const documentTable = useMemo((): {
    docs: { id: number; name: string; url: string }[];
    documentColumns: TableColumn<{
      id: number;
      name: string;
      url: string;
    }>[];
  } => {
    const docForm = steps?.find((e) => e.type === "Documents");

    if (!docForm) return { docs: [], documentColumns: [] };
    return {
      docs: Object.entries(application?.documents || {})
        .map(([k, { document: url, id: documentId }]) => {
          const hit = docForm?.documentConfigs?.find(
            (e) => e.document_config_id === +k
          )?.document_label;

          if (!hit) return null;
          return { name: hit, url, id: documentId };
        })
        .filter((x): x is NonNullable<typeof x> => !!x),
      documentColumns: [
        { name: "Sr. No.", selector: (_, i) => (i || 0) + 1, center: true, grow: 0.5 },
        {
          name: "Document Name",
          // selector: (d) => d.name, 
          cell: (d) => <span className="text-center">{d.name}</span>,
          center: true,
          // width: "380px",
          grow: 3,
        },
        // {
        //   name: "Level 1",
        //   center: true,
        //   cell: () => (
        //     <i className="m-0 fa fa text-lg fa-check-square-o text-green-400"></i>
        //   ),
        // },
        {
          name: "View",
          grow: 1,
          width: "180px",
          center: true,
          cell: (d, i) => (
            <div className="flex space-x-4">
              {/* <button
                className="text-lg"
                disabled={
                  updatingDocDecision || status !== "Pending Level 1 Review"
                }
                onClick={() => {
                  updateDocDecision({
                    applicationId: id,
                    decision:
                      documentDecisions?.[String(d?.id)] === false ? 1 : 0,
                    docId: Number(d.id),
                  });
                }}
              >
                {documentDecisions?.[String(d?.id)] === false ? (
                  <i className="m-0 fa fa-window-close-o text-red-500"></i>
                ) : (
                  <i className="m-0 fa fa-check-square-o text-green-400"></i>
                )}
              </button> */}
              {/* <button
                className="text-lg"
                onClick={() =>
                  setDocument({
                    url: String(d?.url),
                    name: String(d?.name || ""),
                  })
                }
              >
                <i className="m-0 fa fa-eye"></i>
              </button> */}
              {/* <button
                onClick={() => downloadResource(String(d?.url))}
                className="text-lg"
              >
                <i className="m-0 fa fa-download"></i>
              </button> */}
              <DocDecisionL1Button
                disabled={updatingDocDecision}
                isChecked={documentDecisions?.[String(d?.id)] !== false}
                isUpdating={updatingDocDecision}
                statusCheck={status}
                onClick={() => {
                  updateDocDecision({
                    applicationId: id,
                    decision: documentDecisions?.[String(d?.id)] === false ? 1 : 0,
                    docId: Number(d.id),
                  });
                }}
                tooltipText="Toggle Document Decision"
              />
              <ViewButton
                // onClick={() =>
                //   setDocument({
                //     url: String(d?.url),
                //     name: String(d?.name || ""),
                //   })
                // }
                onClick={() => {
                  openInNewTab(String(d?.url));
                }}
              />
              <DownloadButton
                // onClick={() => downloadResource(String(d?.url))}
                onClick={() => simpleDownload(String(d?.url))}
              />
            </div>
          ),
        },
      ],
    };
  }, [
    steps,
    status,
    application,
    updateDocDecision,
    updatingDocDecision,
    documentDecisions,
  ]);

  const nothingToShow = !!(
    !(application?.nocs as Array<Record<string, unknown>>)?.length &&
    !(documentTable?.docs as Array<Record<string, unknown>>)?.length
  );



  const isImageUrl = (doc: { url: string }) => {
    return /\.(jpg|jpeg|png|svg)$/.test(doc.url);
  };

  return (
    <>
      <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
        Documents
      </h4>
      {!!nocColumns?.length &&
        !!(application?.nocs as Record<string, unknown>[])?.length && (
          <><p className=" text-grey-600 text-base font-semibold">
            Noc Table:
          </p><DataTable
              keyField="url"
              className="mt-4 border border-none rounded-none"
              columns={nocColumns}
              customStyles={tableCustomStyles}
              data={(application?.nocs as Record<string, unknown>[]) || []} /></>
        )}
      {!!documentTable.docs?.length && (
        <><p className="text-grey-600 text-base mt-4 font-semibold">
          Document Table:
        </p><DataTable
            keyField="url"
            className="mt-4 border border-none rounded-none"
            data={documentTable.docs}
            customStyles={tableCustomStyles}
            columns={documentTable.documentColumns} /></>
      )}
      {nothingToShow && (
        <div className="flex justify-center items-center py-8">
          <h6 className="text-xl font-semibold">No Documents</h6>
        </div>
      )}
      {!!onNext && (
        <div className="flex justify-center space-x-4 items-center pt-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            disabled={
              nocDecisionMutation.isPending || docDecisionMutation.isPending
            }
            className="bg-black px-10 py-2 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={
              nocDecisionMutation.isPending || docDecisionMutation.isPending
            }
            className="bg-primary px-6 py-2 rounded-full font-bold text-white"
          >
            Next
          </button>
        </div>
      )}
      {!!document && (
        <DocumentPreviewDialog
          open={!!document}
          onClose={() => setDocument(null)}
        >
          {isImageUrl(document) ? (
            <img
              src={document.url}
              className="object-contain mx-auto"
              alt={document.name}
            />
          ) : (
            <Worker workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}>
              <Viewer fileUrl={document.url + '#toolbar=0&zoom=full'} />
            </Worker>
          )}
        </DocumentPreviewDialog>
      )}
    </>
  );
}
